/** Contrato do backend Flask (/api/v1). Envelopes, paginação, erros e entidades.
 *
 * Modelo 100% em inglês (redesenho 2026-07-03): colunas, campos de API e valores
 * enum em inglês. A tabela `status` é a única bilíngue — a API expõe `status`
 * (code inglês) + `status_label` (rótulo pt-BR). Labels da GUI ficam em status.ts.
 */

import type { ClassificacaoFrotaLeve } from '../../shared/classificacaoFrotaLeve'

/** Sucesso: { data, message }. */
export interface Envelope<T> {
  data: T
  message: string
}

/** Listas paginadas: data = { items, total, page, per_page, pages }. */
export interface Paginated<T> {
  items: T[]
  total: number
  page: number
  per_page: number
  pages: number
}

/** Códigos de erro conhecidos (utils/responses.py + JWT). */
export type ApiErrorCode =
  | 'VALIDATION_ERROR'
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  // Recusa de RBAC por ação. O servidor devolve este código em TODO 403 de
  // permissão (auth_utils.require_permission, _crud._perm_denied, users.py), e ele
  // não estava aqui: todo 403 de permissão caía no `default` de apiError e mostrava
  // a mensagem crua, enquanto o branch de FORBIDDEN ficava praticamente morto.
  | 'PERMISSION_DENIED'
  | 'INVALID_CREDENTIALS'
  | 'TOKEN_EXPIRED'
  | 'INVALID_TOKEN'
  | 'UNAUTHORIZED'
  | 'TOKEN_REVOKED'
  | 'FRESH_TOKEN_REQUIRED'
  | 'ALREADY_EXISTS'
  | 'INVALID_STATE_TRANSITION'
  | 'TENANT_CONTEXT_REQUIRED'
  | 'ACCOUNT_BLOCKED'
  | 'ACCOUNT_NOT_ACTIVATED'
  | 'PASSWORD_CHANGE_REQUIRED'
  | 'DEPENDENCY_DOWN'
  | 'INTERNAL_ERROR'
  | 'NETWORK_ERROR'
  | (string & {})

/** Erro normalizado da API — lançado pelo client. */
export class ApiError extends Error {
  code: ApiErrorCode
  status: number
  details?: unknown

  constructor(message: string, code: ApiErrorCode, status: number, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
  }
}

/** Estado de cadastro genérico (contexto `generic` da tabela status). */
export type CadastroStatus = 'active' | 'inactive' | 'blocked' | 'maintenance'

/** Situação de RH apresentada em Pessoas. O status técnico continua separado. */
export type SituacaoColaborador = 'ativo' | 'afastado' | 'ferias' | 'demitido' | 'bloqueado'

// ── Auth ─────────────────────────────────────────────────────────────

export type Role = 'staff_admin' | 'staff_operator' | 'customer_admin' | 'customer_operator'

/** Usuário autenticável (backend app_user). */
export interface Usuario {
  uuid: string
  email: string
  name: string
  phone: string | null
  role: Role
  is_active: boolean
  is_blocked: boolean
  is_staff: boolean
  customer_uuid: string | null
  customer_nome: string | null
  /** Razão social que agrupa as filiais, para apoio discreto do nome. */
  customer_empresa_pai?: string | null
  /** Todos os clientes do usuário do portal, o principal na frente. O mesmo grupo
   *  aparece no cadastro como várias unidades, e quem acompanha o grupo responde
   *  por várias. Vazio para usuário de equipe, que não tem cliente. */
  customers?: { uuid: string; nome: string; empresa_pai?: string | null }[]
  last_login_at: string | null
  created_at: string
  /** Conta criada e ninguém entrou nela ainda: não existe senha definitiva. É o
   *  que a lista do backoffice mostra como "Aguardando 1º acesso". */
  primeiro_acesso_pendente?: boolean
  /** A SESSÃO atual foi aberta com a senha temporária do e-mail. Vem do claim do
   *  token, em /login e /me, e prende a interface na criação da senha. */
  senha_temporaria?: boolean
  /** Permissões finas concedidas (RBAC por ação). Presente em /login, /me e no
   *  CRUD de usuários. staff_admin ignora a lista (is_superuser). */
  permissions?: string[]
  /** Com cargo, `permissions` são as EFETIVAS, as do cargo somadas. O formulário de
   *  usuário edita só as próprias, que com cargo são os adicionais. */
  permissions_proprias?: string[]
  permissions_do_cargo?: string[]
  /** staff_admin = acesso total (bypass no backend). */
  is_superuser?: boolean
  /** As áreas EFETIVAS (24/10/2026): concedidas pelo cargo ou pelo próprio
   *  usuário, e com ao menos uma leitura de módulo com tela. É o que decide os
   *  cards da tela de áreas e o destino depois do login. Cliente recebe vazio. */
  areas?: string[]
  /** O que o formulário edita: as áreas gravadas no próprio usuário, que com
   *  cargo são as adicionais, e as que vêm do cargo, travadas. */
  areas_proprias?: string[]
  areas_do_cargo?: string[]
  /** O cargo do operador da equipe (15/10/2026); as permissões dele valem para o
   *  usuário além das próprias. */
  cargo?: { uuid: string; nome: string } | null
  /** Escopo por ITENS: modo e contagem por categoria. A LISTA de itens não vem
   *  aqui — um usuário pode ter milhares de vínculos, e a tela carrega a página
   *  que precisa por `/users/<uuid>/escopo/<categoria>`. */
  scope?: UsuarioScopes
  /** As duplas ATIVAS do usuário de equipe, em /login e /me (01/10/2026). Com uma
   *  só, os filtros de Dupla das telas abrem nela; ver `useDuplaPadrao`. */
  duplas?: { uuid: string; nome: string }[]
}

/** Modo de acesso de UMA categoria. `all` inclui o que for cadastrado depois. */
export interface ScopeResumo {
  all: boolean
  count: number
}

/** As cinco categorias de vínculo, como o backend as devolve, e as duplas, cujo
 *  `all` é a marca "todas as duplas" (08/10/2026). */
export interface UsuarioScopes {
  customers: ScopeResumo
  contracts: ScopeResumo
  personnel: ScopeResumo
  fleet: ScopeResumo
  places: ScopeResumo
  duplas?: ScopeResumo
}

/** Lado de aplicação de uma permissão/pastinha. */
export type PermissionSide = 'staff' | 'customer' | 'both'

/** Uma permissão fina (ação) dentro de uma pastinha. */
export interface PermissionDef {
  key: string
  label: string
  side: PermissionSide
}

/** Pastinha (módulo) do catálogo de permissões. */
export interface PermissionModule {
  key: string
  label: string
  side: PermissionSide
  /** As áreas do módulo (24/10/2026). A permissão só vale com uma delas concedida.
   *  Vazio = módulo compartilhado, que nenhuma área filtra, como o assistente. */
  areas?: string[]
  /** Módulo sem tela no menu, como a importação em massa: não abre área sozinho. */
  sem_tela?: boolean
  permissions: PermissionDef[]
}

/** Uma área do catálogo do servidor. */
export interface AreaDoCatalogo {
  key: string
  label: string
  descricao: string
}

/** GET /permissions/catalog — pastinhas para o formulário de usuário. */
export interface PermissionCatalog {
  modules: PermissionModule[]
  /** Conjunto padrão por papel, para o formulário PRÉ-SELECIONAR ao criar.
   *
   *  O servidor só aplica o default quando a chave `permissions` está ausente, e o
   *  formulário sempre mandava lista vazia: usuário criado pela interface entrava
   *  com a barra lateral vazia. */
  defaults?: Record<string, string[]>
  /** As áreas, na ordem dos cards. Só para a equipe. */
  areas?: AreaDoCatalogo[]
  /** As áreas pré-marcadas ao criar usuário sem cargo, por papel. */
  areas_padrao?: Record<string, string[]>
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  usuario: Usuario
  /** Papel exige segundo fator e a pessoa ainda não configurou. A sessão só
   *  alcança as rotas de configuração enquanto isto for verdadeiro. */
  mfa_setup_required?: boolean
  /** Entrou com a senha temporária do e-mail. A sessão só alcança a criação da
   *  senha definitiva enquanto isto for verdadeiro. */
  senha_temporaria?: boolean
  /** Entrou com código de recuperação: convém regenerar o lote. */
  recovery_code_used?: boolean
  recovery_codes_left?: number
}

export interface RefreshResponse {
  access_token: string
}

/** GET /dashboard/backoffice */
export interface BackofficeDashboard {
  tickets_por_status: Record<string, number>
  shipments_por_status: Record<string, number>
  shipments_hoje: number
}

/** Destino dentro de um contrato: a folha da hierarquia de Cargas Solicitadas.
 *  `key` ignora acento e caixa, e `nome` vem em maiúsculas, a mesma régua da
 *  Lista de Descarga. */
export interface DestinoSolicitado {
  key: string
  nome: string
  total: number
  escaladas: number
}

/** Contrato dentro de um cliente, com a quebra por destino. */
export interface ContratoSolicitado {
  contract_uuid: string | null
  contract_number: string | null
  total: number
  escaladas: number
  /** uuid das duplas que respondem pelo contrato. A do contrato manda; sem ela,
   *  valem as do cliente. */
  duplas: string[]
  destinos: DestinoSolicitado[]
}

/** Cliente dentro de uma região, com a quebra por contrato. */
export interface ClienteRegiao {
  customer_uuid: string | null
  nome: string | null
  total: number
  escaladas: number
  contratos: ContratoSolicitado[]
}

/** Região, que é o município, com a quebra por cliente. `key` = str(city_id). */
export interface RegiaoSolicitada {
  key: string
  city: string | null
  uf: string | null
  total: number
  escaladas: number
  clientes: ClienteRegiao[]
}

/** Local + total (quebra dentro de um status). */
export interface LocalCount {
  local: string
  total: number
  /** O filtro que mostra as mesmas linhas no Painel de Descarga. Local atual é
   *  seletor, de valor exato; destino é busca no Local de descarga. Vazio quando a
   *  linha não tem destino, e o clique leva só o status. */
  painel?: { local?: string; local_atual?: string[] }
}

/** GET /dashboard/backoffice/descarga — a frota de hoje, só os conjuntos alocados
 *  em dupla: contagem por status e quebra status × local. */
export interface DescargaResumo {
  total: number
  status_counts: Record<string, number>
  locais_por_status: Record<string, LocalCount[]>
  /** Rótulos de local distintos p/ o dropdown de filtro. */
  locais?: string[]
  /** As duplas que têm conjunto alocado, para o seletor. */
  duplas?: { uuid: string; name: string }[]
  descarga_ref_date?: string
}

/** GET /dashboard/backoffice/operacao — as cargas solicitadas da data. */
export interface DashboardOperacao {
  solicitadas_ref_date: string
  cargas_solicitadas: {
    total: number
    escaladas: number
    sem_data: number
    regioes: RegiaoSolicitada[]
    /** As duplas que alguma carga da data alcança, para o seletor. */
    duplas: { uuid: string; name: string }[]
  }
}

/** GET /dashboard/cliente */
/** GET /dashboard/cliente — o painel de acompanhamento do cliente. Tudo já
 *  recortado pelo cliente do usuário e pelo gate de SML no servidor. */
export interface ClienteDashboardIndicadores {
  cargas_hoje: number
  em_transito: number
  entregues_hoje: number
  em_andamento: number
  concluidas_30d: number
  nao_atendidas_30d: number
}
export interface ClienteDashboardDia {
  date: string
  programadas: number
  concluidas: number
  nao_atendidas: number
}
export interface ClienteDashboardFrente {
  uuid: string
  name: string
  contract_number: string | null
  producer: string | null
  farm: string | null
  destination: string | null
  hectares: number | null
  estimated_shipments: number | null
  situacao: string
  total: number
  feitas: number
  em_andamento: number
  nao_atendidas: number
}
export interface ClienteDashboard {
  hoje: string
  indicadores: ClienteDashboardIndicadores
  cargas_por_etapa: Record<string, number>
  por_dia: ClienteDashboardDia[]
  hoje_cargas: Shipment[]
  proximas_entregas: Shipment[]
  contratos: ClienteDashboardFrente[]
  contratos_total: number
  tickets_por_status: Record<string, number>
}

// ── Refs enxutas ─────────────────────────────────────────────────────

/** Categoria (fleet/personnel/place/asset) — lookup por FK. */
export interface Categoria {
  uuid: string
  code: string
  label: string
  sort_order?: number
  active?: boolean
}

/** Status universal (context + code) — CRUD em /status. */
export interface Status {
  uuid: string
  context: string
  code: string
  label_en: string
  label_pt: string
  sort_order: number
  active: boolean
}

/** Referência a um place (origem/destino). */
export interface PlaceRef {
  uuid: string
  name: string
  city: string | null
  uf: string | null
  /** Coordenada do local, quando cadastrada: é dela que sai o Link Destino do
   *  cabeçalho da frente e da mensagem do motorista (04/09/2026). */
  latitude?: number | null
  longitude?: number | null
}

/** Função exercida no HUB, independente do cargo que vem do Protheus. No ERP,
 *  motorista, manobra e prancheiro têm todos o cargo MOTORISTA. NÃO confundir com
 *  `category`, que continua sendo driver/operator/producer e decide em que grade a
 *  pessoa aparece. */
export type FuncaoOperacional = 'motorista' | 'manobra' | 'prancheiro' | 'operador'

export interface PersonRef {
  uuid: string
  name: string
  funcao_operacional?: FuncaoOperacional | null
  funcao_operacional_label?: string | null
  situacao?: SituacaoColaborador | null
  situacao_label?: string | null
  /** Até quando dura a ausência cadastrada no DP, o último dia ausente. */
  ausencia_ate?: string | null
}

export interface FleetRef {
  uuid: string
  plate: string | null
  name: string | null
  /** Tipo do veículo. É o que define o ícone exibido junto da placa. */
  category?: Categoria | null
}

/** Referência enxuta a um contrato (usada em ticket/shipment). */
export interface ContractRef {
  uuid: string
  name: string
  contract_number: string | null
}

// ── Cadastros (modelo em inglês) ─────────────────────────────────────

export interface Endereco {
  street: string | null
  number: string | null
  district: string | null
  zip: string | null
}

/** Empresa cliente (backend customer). */
export interface Cliente {
  uuid: string
  legal_name: string
  trade_name: string | null
  cnpj: string | null
  email: string | null
  phone: string | null
  address: Endereco
  city_id: number | null
  state_id: number | null
  city: string | null
  uf: string | null
  status: CadastroStatus
  status_label?: string
  notes: string | null
  created_at: string | null
}

export interface Asset {
  uuid: string
  category: Categoria | null
  customer_uuid: string | null
  name: string
  unit: 'ton' | 'sacas' | 'kg'
  status: CadastroStatus
  status_label?: string
}

export interface Place {
  uuid: string
  category: Categoria | null
  customer_uuid: string | null
  name: string
  code: string | null
  city_id: number | null
  state_id: number | null
  city: string | null
  uf: string | null
  latitude: number | null
  longitude: number | null
  /** Raio da geocerca do local em km. null = padrão global de 1 km (só no círculo). */
  geofence_radius_km?: number | null
  /** Forma da cerca: círculo (ponto+raio) ou polígono (anel desenhado). */
  geofence_type?: 'circle' | 'polygon'
  /** Anel do polígono [[lng,lat],...] (ordem GeoJSON) quando geofence_type = polygon. */
  geofence_polygon?: [number, number][] | null
  address: string | null
  /** Link fixo do local (04/09/2026): o hotel da hospedagem do maquinário lê daqui. */
  link?: string | null
  contact?: string | null
  notes?: string | null
  /** Forma padrão de cobrança da hospedagem (só faz sentido na categoria hotel).
   *  Copiada como snapshot para cada registro de hospedagem na criação. */
  lodging_billing_mode?: 'semanal' | 'quinzenal' | 'mensal' | 'final' | null
  lodging_billing_notes?: string | null
  /** nº de contratos/pivôs que têm este local como fazenda (origem). */
  contracts_count?: number
  /** hectares somados dos contratos/pivôs deste local (fazenda). */
  hectares?: number | null
  status: CadastroStatus
  status_label?: string
}

/** Rastreador TrucksControl: cadastro + última posição (1:1, sem histórico).
 *  `category` vem do fleet casado por placa; null = equipamento fora do cadastro. */
export interface TrackerDevice {
  uuid: string
  vei_id: number
  placa: string | null
  chassi: string | null
  eqp: number | null
  fleet_uuid: string | null
  fleet_name: string | null
  category: Categoria | null
  lat: number | null
  lon: number | null
  velocidade: number | null
  /** evt4: -1 desconhecido / 0 desligada / 1 ligada. */
  ignicao: number | null
  voltagem: number | null
  odometro: number | null
  municipio: string | null
  uf: string | null
  rodovia: string | null
  rua: string | null
  motorista: string | null
  /** dt da última mensagem (hora no veículo, ISO com fuso). */
  msg_dt: string | null
  /** Sensor de temperatura 1, em °C. Null quando o equipamento não tem sensor. */
  temperatura: number | null
  /** dt da mensagem que trouxe a última leitura de temperatura. */
  temperatura_dt: string | null
  updated_at: string | null
  /** Status da viagem do caminhão, correlacionado frota↔rastreador pela placa e
   *  replicando o Painel de Descarga. Ausente quando o caminhão não está em conjunto. */
  viagem?: DeviceViagem | null
  /** Cerca ativa mais próxima que contém a posição atual. Garagem desde 08/10/2026. */
  cerca?: { tipo: 'contrato' | 'ubs' | 'garagem'; nome: string; uuid: string } | null
}

/** Status operacional do caminhão (unificado com o Painel) no painel de Dispositivos. */
export interface DeviceViagem {
  /** Origem do status: `descarga` = ciclo de grãos (Painel de Descarga); `transporte`
   *  = viagem de prancha, cujo status do VEÍCULO (escalado/embarcando/transito/
   *  disponivel) substitui o de descarga para pranchas em viagem (Samuel). Ausente em
   *  payload antigo → tratar como `descarga`. */
  kind?: 'descarga' | 'transporte'
  status: DescargaStatus | string
  status_label: string | null
  trip_status: string | null
  /** Instante ISO de entrada no status ATUAL. Reinicia a cada transição; null quando
   *  não há registro no histórico. Não tem relação com a conexão do rastreador. */
  status_since: string | null
  /** Instante ISO do escalonamento que abriu o ciclo atual. Preservado enquanto a
   *  viagem avança (Carregando, Trânsito, Descarregando) e null fora dessas etapas. */
  escalado_em: string | null
  /** Data e hora PROGRAMADA da carga (coluna "Data / Hora" da Frente de Trabalho) —
   *  o que a coluna "Escalado em" do painel mostra (Samuel), não o clique de
   *  escalonamento. null quando o conjunto não tem carga ativa. Na prancha
   *  (`kind='transporte'`) é a data de INÍCIO da viagem de máquina, `YYYY-MM-DD`
   *  sem hora: quem exibe não pode ler isso como UTC. */
  programado_para?: string | null
  contract: string | null
  contract_uuid: string | null
  /** Origem e destino da ESCALA em que o veículo está, pela mesma cadeia da coluna
   *  de local do Painel de Descarga. São nomes de lugar de negócio, fazenda e UBS,
   *  não a posição atual do rastreador, que vive em `municipio`/`uf`.
   *
   *  Destino: ajuste manual do dia, destino da carga, destino do contrato. Origem:
   *  origem da carga, fazenda do contrato. Conjunto liberado como Disponível perde
   *  os dois na mesma hora que o status muda. */
  origem?: string | null
  destino?: string | null
  customer_uuid: string | null
  customer_name: string | null
  dupla_uuid: string | null
  dupla_name: string | null
  /** Conjunto de caminhão da linha. Ausente em payload antigo. */
  conjunto_uuid?: string | null
  /** De quem é a jornada da linha, nesta precedência: quem se identificou no
   *  terminal do rastreador, o motorista da viagem de prancha, o titular do
   *  conjunto. Digitar a senha no teclado e abrir a jornada na ATS são o mesmo ato,
   *  então no caso comum é o mesmo nome de `motorista`, que vem da macro. */
  driver?: string | null
  /** Chave do dialog de batidas nesta tela. É o MOTORISTA e não o conjunto: a rota
   *  do conjunto exige isolamento por dupla, que a lista do mapa não aplica, e as
   *  duas regras discordando faziam o mapa listar o veículo e recusar o detalhe. */
  driver_uuid?: string | null
  /** Jornada ATS de `driver`, a mesma informação da coluna do Painel de Descarga.
   *  Ausente em payload antigo. */
  jornada?: PainelJornada | null
}

/** Uma transmissão do histórico GPS (GET /rastreamento/trajeto). É a matéria-
 *  prima do Rastro no mapa: coordenada sempre presente e plausível, telemetria
 *  crua da mensagem para o popup do ponto. */
export interface TrackerPositionPoint {
  msg_id: number | null
  observed_at: string
  lat: number
  lon: number
  velocidade: number | null
  /** evt4: -1 desconhecido / 0 desligada / 1 ligada. */
  ignicao: number | null
  voltagem: number | null
  odometro: number | null
  temperatura: number | null
  municipio: string | null
  uf: string | null
  rodovia: string | null
  rua: string | null
  /** Quem estava com a senha no terminal neste ponto (08/10/2026): a última
   *  identificação do motorista no veículo até aqui. `motorista` vem nulo quando o
   *  motID não tem cadastro no HUB, e aí a tela mostra o número. */
  motorista: string | null
  motorista_mot_id: number | null
  /** Só no rastro das máquinas e da frota leve (14/10/2026): o endereço que o
   *  rastreador manda, no lugar de município, UF, rodovia e rua. */
  endereco?: string | null
}

/** Resposta do trajeto: pontos em ordem cronológica. `truncado` avisa que o
 *  teto de pontos cortou o fim do período — nunca um corte silencioso. */
export interface TrajetoResposta {
  veiculo: { uuid: string; plate: string | null; name: string | null }
  items: TrackerPositionPoint[]
  total: number
  truncado: boolean
  de: string
  ate: string
}

/** Camadas das máquinas, do SSX, e da frota leve, da Aldeia (14/10/2026). */
export type CamadaRastreador = 'maquinas' | 'frota_leve'

/** Pessoa do HUB citada na unidade: nome e o uuid do cadastro de Pessoas. */
export interface PessoaDaUnidade {
  nome: string | null
  personnel_uuid: string | null
}

/** A última posição de uma máquina ou de um utilitário rastreado
 *  (GET /rastreamento/unidades). `observado_em` é a última mensagem de qualquer
 *  tipo; `posicao_em`, a da última posição plausível, a que está no mapa. */
export interface UnidadeRastreada {
  uuid: string
  camada: CamadaRastreador
  /** Desde 20/10/2026 a aba Máquinas também traz as máquinas do Trucks Control. */
  fornecedor: 'ssx' | 'aldeia' | 'truckscontrol'
  codigo_origem: string
  identificacao: string | null
  placa: string | null
  frota: {
    uuid: string
    rotulo: string | null
    placa: string | null
    modelo: string | null
    marca: string | null
    categoria: string | null
    /** O tipo do veículo no ERP, como COLHEITADEIRA OXBO: o Tipo da aba Máquinas. */
    tipo?: string | null
    classificacao_frota_leve?: ClassificacaoFrotaLeve | null
  } | null
  vinculo: 'patrimonio' | 'placa' | 'chassi' | 'manual' | 'ambiguo' | null
  lat: number | null
  lon: number | null
  observado_em: string | null
  posicao_em: string | null
  recebido_em: string | null
  velocidade: number | null
  ignicao: boolean | null
  ignicao_desde: string | null
  gps_valido: boolean | null
  endereco: string | null
  /** O Motorista Atual: o do último cartão lido no rastreador, e só ele. */
  motorista: (PessoaDaUnidade & { origem?: 'rastreador' }) | null
  /** Só na frota leve, o Motorista Conjunto: o responsável do conjunto de utilitário
   *  ativo em Conjuntos. Independente do Motorista Atual. */
  responsavel?: PessoaDaUnidade | null
  /** O cliente da programação de colheita de hoje, ou vazio sem ela. */
  cliente: { uuid: string; nome: string | null } | null
  /** A programação de colheita de hoje (Samuel, 20/10/2026): a da máquina ou, na
   *  frota leve, a do Motorista Conjunto. Mais de uma no dia, a cadastrada primeiro. */
  programacao: {
    contract_uuid: string
    contrato: string | null
    frente: string | null
    operador: string | null
    colheita_uuid: string
    hora: string | null
  } | null
}

/** O estado de cada coleta (GET /rastreamento/coleta), sem valor de configuração. */
export interface ColetaRastreador {
  fornecedor: 'ssx' | 'aldeia'
  camada: CamadaRastreador
  habilitado: boolean
  configurado: boolean
  ultima_coleta_em: string | null
  cadastro_em: string | null
  ultimo_erro: { em: string | null; tipo: string | null; http: number | null } | null
  unidades: number
  com_posicao: number
  vinculadas: number
  transmitindo: number
  ultima_posicao_em: string | null
}

/** Item móvel do cadastro de frota e máquinas. */
export interface Fleet {
  uuid: string
  category: Categoria | null
  customer_uuid: string | null
  name: string | null
  code: string | null
  filial: string | null
  plate: string | null
  brand: string | null
  model: string | null
  year: number | null
  capacity: number | null
  /** Eixos do veículo, para o esperado do pedágio (14/10/2026). Vazio quando ninguém informou. */
  eixos?: number | null
  /** Classificação da frota leve no Mapa Operacional (20/10/2026). Só Frota Leve,
   *  Fazenda e Outros a recebem. */
  classificacao_frota_leve?: ClassificacaoFrotaLeve | null
  renavam: string | null
  owner: string | null
  on_leave: boolean
  status: CadastroStatus
  status_label?: string
  notes: string | null
  /** Chassi. Chave natural que não muda na vida do veículo, ao contrário da placa. */
  chassi?: string | null
  /** Quem é dono do cadastro. Vem do SERVIDOR: é ela que diz quais campos travar. */
  origem?: 'Protheus' | 'HUB'
  /** Preenchido quando a linha é espelho do cadastro de veículos do Protheus. */
  protheus_code?: string | null
  protheus_tipo_veiculo?: string | null
  /** Rodado como vem do ERP. É ele que diz cavalo, reboque ou utilitário. */
  protheus_tipo_rodado?: string | null
  /** Modalidade do ERP. É a única fonte da categoria exibida no HUB. */
  protheus_modalidade?: string | null
  /** Acoplamento cru na origem: SOLTEIRO ou ENGATADO. */
  protheus_situacao?: string | null
  /** Código do reboque acoplado, quando a origem informa engate. */
  protheus_reboque?: string | null
  protheus_conjunto_em?: string | null
  protheus_synced_at?: string | null
}

/** Pessoa (motorista/operador/produtor). Une motoristas+operadores+produtores. */
export interface Personnel {
  uuid: string
  category: Categoria | null
  customer_uuid: string | null
  name: string
  document: string | null
  phone: string | null
  code: string | null
  filial: string | null
  cnh_number: string | null
  cnh_category: string | null
  cnh_expires_at: string | null
  employment: 'freelancer' | 'fleet' | null
  carrier: string | null
  place: PlaceRef | null
  city_id: number | null
  state_id: number | null
  city: string | null
  uf: string | null
  status: CadastroStatus
  status_label?: string
  /** Leitura de RH derivada de situação, admissão e demissão do Protheus. */
  situacao_colaborador?: SituacaoColaborador
  situacao_colaborador_label?: string
  /** O último dia da ausência cadastrada no DP. Nulo sem previsão ou sem ausência. */
  ausencia_ate?: string | null
  /** Desde quando o ERP publica a situação atual, em ISO, e há quantos dias.
   *  Nulos enquanto o HUB não tiver visto a situação mudar, que é como a coluna
   *  nasce para o cadastro inteiro. O endpoint de pessoas não manda período de
   *  férias nem de afastamento, então esta é a única medida de há quanto tempo
   *  uma ausência dura. */
  situacao_desde?: string | null
  situacao_ha_dias?: number | null
  notes: string | null
  /** Função exercida no HUB. Editável mesmo em pessoa espelhada do Protheus: é o
   *  campo do HUB, e o cargo do ERP fica em `protheus_funcao`. */
  funcao_operacional?: FuncaoOperacional | null
  funcao_operacional_label?: string | null
  /** O que ESTA pessoa pode exercer, decidido pelo servidor. Operador é apenas
   *  operador; só quem veio do cadastro de motoristas circula entre Motorista,
   *  Manobra e Prancheiro. */
  funcoes_disponiveis?: FuncaoOperacional[]
  /** Só de ESCRITA: a data em que a função nova passa a valer. Pode ser no passado,
   *  nunca no futuro. Não volta na leitura. */
  funcao_vigente_de?: string
  /** Quem é o dono da linha, dito pelo servidor: `Protheus` ou `HUB`. A grade exibe
   *  este valor em vez de deduzi-lo da presença do código. */
  origem?: 'Protheus' | 'HUB'
  /** De para com o Protheus. Qualquer um dos dois códigos preenchido = a linha é
   *  ESPELHO do ERP: nome, matrícula, filial, CPF, telefone, município e situação
   *  vêm de lá e o formulário os trava. CNH, vínculo, transportadora e observações
   *  continuam do HUB. Use `origem`, que o servidor já resolve. */
  protheus_code: string | null
  /** Código no cadastro de OPERADORES do ERP, que é outro espaço de numeração: a
   *  mesma pessoa tem número diferente nos dois, e há códigos que coincidem
   *  apontando para pessoas diferentes. As duas colunas podem conviver. */
  protheus_operador_code?: string | null
  protheus_funcao: string | null
  protheus_base: string | null
  /** Situação crua na origem: ATIVO, INATIVO, AFASTADO ou FERIAS. O status técnico
   *  reduz AFASTADO e FERIAS a `blocked`; `situacao_colaborador` preserva o rótulo
   *  que o RH precisa ver. */
  protheus_situacao: string | null
  protheus_synced_at: string | null
  /** Vínculo empregatício como o ERP o publica, em ISO. `demissao` nula é quem
   *  está na ativa, não dado faltando: não marcar como pendência. */
  admissao?: string | null
  demissao?: string | null
  /** `document` e `phone` vieram mascarados: falta a permissão master_data.pii.
   *  O servidor também recusa gravá-los nesse caso, então a tela desabilita os dois. */
  pii_masked?: boolean
}

/** O ciclo de sincronização em curso, para a tela dizer qual é, desde quando e
 *  quem pediu, e para o botão de cancelar (Samuel e Amorim, 04/09/2026). */
export interface ProtheusSincronizacaoAndamento {
  /** Identifica o clique: a tela procura este id em `ultima_manual`. */
  id: string
  origem: 'ciclo' | 'manual'
  entidades: string[]
  atual: string | null
  inicio: string
  usuario: string | null
  cancelamento_pedido: boolean
}

export interface ProtheusSincronizacaoItem {
  entidade: string
  ok: boolean
  contagem?: { lidos: number | null; criados: number | null; atualizados: number | null; inalterados: number | null }
  motivo?: string
  mensagem?: string
}

/** O último disparo do botão já terminado, com o resultado por cadastro. */
export interface ProtheusSincronizacaoResultado extends ProtheusSincronizacaoAndamento {
  itens: ProtheusSincronizacaoItem[]
  fim: string
}

export interface ProtheusSincronizacao {
  andamento: ProtheusSincronizacaoAndamento | null
  /** A frase pronta do servidor: "Sincronização de frota em andamento desde 14:02, pedida por Cida". */
  descricao: string | null
  /** Ausente no 202 do disparo, que só devolve o andamento. */
  ultima_manual?: ProtheusSincronizacaoResultado | null
}

/** Resumo do último ciclo de sincronização com o Protheus. */
export interface ProtheusSyncResumo {
  synced_at: string | null
  motoristas: number
  criados: number
  atualizados: number
  inalterados: number
}

// ── Contract / fluxo operacional ─────────────────────────────────────

/** Situação do contrato, marcada à mão no cabeçalho da Frente: manual desde
 *  24/09/2026, quando deixou de ser derivada das cargas. Sem marca é `previsto`.
 *  `aguardando_liberacao` entrou no mesmo dia. */
export type ContractSituacao =
  | 'previsto' | 'aguardando_liberacao' | 'em_execucao' | 'paralisado' | 'finalizado'
  | 'finalizado_com_pendencias'

export interface ContractMetricas {
  shipments_total: number
  shipments_feitos: number
  shipments_p_finalizar: number | null
  estimated_shipments: number | null
  pct_colheita: number | null
  media_ha_shipment: number | null
  ocorrencias: number
  situacao: ContractSituacao
  /** Cargas ESCALADAS sobre as solicitadas da frente e o percentual disso: o
   *  mesmo número do Dashboard, que a barra lateral passou a mostrar
   *  (Samuel, 04/09/2026). */
  shipments_escaladas?: number
  pct_escaladas?: number | null
  /** As solicitadas da conta acima: o total sem as Não atendidas (Samuel,
   *  30/10/2026). `shipments_total` segue com elas, porque é o total da grade. */
  shipments_solicitadas?: number
}

/** O que o ERP publica para a frente vinculada. O cabeçalho mostra o valor do
 *  ERP e o do cadastro só quando o ERP não tem o dado. */
export interface ContractErp {
  km: number | null
  pedagio: number | null
  hectares: number | null
  safra: string | null
}

/** Uma divergência entre o cadastro e o ERP, para o sino do cabeçalho. */
export interface ContractAnomalia {
  codigo: 'pedagio' | 'km' | 'hectares'
  hub: number
  erp: number
}

/** Contrato do cliente (1 aba da planilha = 1 contrato). Backend: `contract`. */
export interface Contract {
  uuid: string
  name: string
  contract_number: string | null
  customer_uuid: string | null
  /** O Cliente da operação: a filial, pelo nome fantasia. */
  customer_nome: string | null
  /** Razão social que agrupa as filiais. Vazia quando o cliente não tem fantasia
   *  própria — aí `customer_nome` já é a razão social. */
  customer_empresa_pai?: string | null
  /** Cooperante — string simples (não é mais pessoa do sistema). */
  producer: string | null
  farm: PlaceRef | null
  destination: PlaceRef | null
  /** Município do contrato (região da carga no Dashboard). Fallback: cidade da fazenda. */
  city_id: number | null
  state_id: number | null
  city: string | null
  uf: string | null
  asset: Asset | null
  season: string | null
  hybrid: string | null
  hectares: number | null
  km: number | null
  toll: number | null
  share: string | null
  estimated_shipments: number | null
  latitude: number | null
  longitude: number | null
  /** Par do Ponto de Apoio/Sede da fazenda. Par preenchido gera o local
   *  Apoio/Sede na importação; o link da Sede na mensagem deriva daqui. */
  sede_latitude: number | null
  sede_longitude: number | null
  /** Raio customizado; null ou ausente usa o padrão operacional de 1 km. */
  geofence_radius_km?: number | null
  /** Campos operacionais da planilha. */
  informacao_complementar: string | null
  /** Campo legado combinado (preservado no banco); a UI usa filial_tms/filial_gct. */
  filial_gct_tms: string | null
  filial_tms: string | null
  filial_gct: string | null
  /** Contrato jurídico (texto longo — cabeçalho da Frente, Mudanças v1 §2). */
  contrato_juridico: string | null
  rc_operacao: string | null
  /** Frente (código — cabeçalho da Frente de Trabalho). Obrigatório na criação. */
  frente: string | null
  tipo_servico: string | null
  localizacao_link: string | null
  /** Flag antiga de "finalizado com pendência", do tempo da situação derivada. Não
   *  decide mais nada desde 24/09/2026: a situação é `situacao_override`. */
  pendencia_manual: boolean
  /** A situação marcada no cabeçalho da Frente; null = Previsto. */
  situacao_override: ContractSituacao | null
  legal: Record<string, unknown> | null
  commercial: Record<string, unknown> | null
  file_url: string | null
  status: 'active' | 'closed'
  status_label?: string
  notes: string | null
  created_at: string | null
  /** Subcontrato do Protheus que originou esta frente, o trilho de TRANSPORTE.
   *  Tudo que vem do ERP é lido daqui e nunca copiado para campo do contrato.
   *  null = frente legada, ainda sem par no ERP, e é essa ausência que a tela lista
   *  como pendência. */
  subcontrato?: SubcontratoRef | null
  /** Planilha de campo que esta frente representa, o trilho GCT. Simétrico do
   *  subcontrato acima, e a maioria das frentes não tem: só 3 das 61 do ERP
   *  carregam os dois lados. null aqui NÃO é pendência. */
  planilha?: PlanilhaRef | null
  /** Frentes do ERP que este contrato representa, uma por linha. O Protheus abre a
   *  mesma operação ora numa frente com os dois trilhos, ora em duas frentes irmãs,
   *  uma por trilho: para o HUB é um contrato só, e ele lembra das duas. `trilho`
   *  diz o lado que cada uma trouxe. Número em texto, e não referência: a frente
   *  some da carga quando o ERP a encerra, e o número precisa sobreviver a isso. */
  frentes_protheus?: ContractFrenteVinculo[]
  /** Presente na lista do cockpit e no detalhe. */
  metricas?: ContractMetricas
  erp?: ContractErp | null
  anomalias?: ContractAnomalia[]
  /** A dupla responsável, lida de `dupla_contrato` (Samuel, 15/09/2026). Com dupla,
   *  o contrato aparece só na Frente de Trabalho dela; sem dupla, na de quem tem
   *  o cliente no escopo. */
  dupla?: { uuid: string; name: string } | null
  /** A Operação e a Semana da planilha de planejamento ligada ao contrato, que o
   *  formulário edita (Samuel, 30/10/2026). Só no detalhe do contrato. */
  planejamento?: {
    na_planilha: boolean
    operacao: string | null
    semana_colheita: string | null
    semana_original: string | null
  }
}

export type Prioridade = 'low' | 'normal' | 'high' | 'urgent'
export type TicketStatus =
  | 'draft' | 'submitted' | 'under_review' | 'approved' | 'in_progress' | 'completed' | 'cancelled'
export type ShipmentStatus =
  | 'draft' | 'awaiting_harvest' | 'loading' | 'awaiting_invoice' | 'awaiting_unload'
  | 'unloading' | 'released' | 'cancelled'

/** Modalidade da solicitação/carga (code da categoria `modality`). */
export type Modalidade = 'colheita_transporte' | 'colheita' | 'transporte' | 'movimentacao_maquina'

/** Pedido de embarque (backend ticket, era solicitacao). */
export interface Ticket {
  uuid: string
  number: string
  customer_uuid: string | null
  customer_nome: string | null
  /** Razão social que agrupa as filiais, para apoio discreto do nome. */
  customer_empresa_pai?: string | null
  contract: ContractRef | null
  /** Modalidade (categoria) — null nos tickets antigos (tratados como colheita_transporte). */
  modality?: Categoria | null
  asset: Asset | null
  origin: PlaceRef | null
  destination: PlaceRef | null
  quantity: number
  /** Uma entrada por carga informada pelo cliente. Além do horário, cada carga
   *  pode trazer data/origem/destino próprios (override; ausentes → header do ticket). */
  cadence:
    | { seq: number; start_time: string | null; date?: string | null; origin_place_uuid?: string | null; destination_place_uuid?: string | null }[]
    | null
  start_time: string | null
  interval_min: number | null
  load_time_min: number | null
  transit_est_min: number | null
  volume_per_shipment: number | null
  desired_date: string | null
  priority: Prioridade
  status: TicketStatus
  status_label?: string
  notes: string | null
  created_at: string | null
  /** Presente no GET /tickets/:uuid. */
  shipments?: Shipment[]
}

/** Uma divergência da carga, com os dois lados: o que o HUB tem e o que o ERP
 *  publica na viagem equiparada. */
export interface ShipmentAnomalia {
  codigo: 'conjunto_motorista' | 'conjunto_cavalo' | 'conjunto_carreta'
  hub: string | null
  erp: string | null
}

/** Viagem gerada do ticket (backend shipment, era carga). Absorve a escala D-1. */
export interface Shipment {
  uuid: string
  number: string
  ticket_uuid: string | null
  customer_uuid: string | null
  contract_uuid: string | null
  contract: string | null
  /** Modalidade (categoria) — separa colheita/transporte. */
  modality?: Categoria | null
  asset: Asset | null
  seq: number | null
  origin: PlaceRef | null
  destination: PlaceRef | null
  truck_plate: string | null
  trailer_plate: string | null
  trip: string | null
  ciot: string | null
  /** SML (averbação de risco) — preenchido, libera a carga ao cliente. */
  sml: string | null
  vp_number: string | null
  /** Solicitação VP no Protheus (solicitado/nao_solicitado/aguardando_sync/erro_sync)
   *  — read-only na grade; sincronizado pelo backend (TODO(protheus)). */
  vp_request_status: string | null
  /** Última sincronização da Solicitação VP (tooltip da tag). */
  vp_request_synced_at: string | null
  invoice: string | null
  cte_batch: string | null
  deviation: string | null
  incident: string | null
  /** Status nota (invoice_status) e Status viagem (trip_status) — codes de catálogo.
   *  trip_status é a máquina operacional única (Complemento §1/§5). */
  invoice_status: string | null
  trip_status: string | null
  /** O motivo próprio do Não atendido, do cadastro Motivos de não atendimento, e
   *  a cobrança decidida ao encerrar (Samuel, 04/09/2026). Nulos fora dele. */
  nao_atendido_motivo?: string | null
  cobranca?: boolean | null
  /** Onde o cavalo está agora, pela posição mais recente do rastreador, a mesma
   *  leitura do Local atual do Painel de Descarga. Só na listagem da equipe; nulo
   *  sem cavalo, sem posição nas últimas 24 horas ou sem município. */
  local_atual?: { municipio: string; uf: string | null; msg_dt: string; lat?: number | null; lon?: number | null } | null
  /** Viagens do ERP que o usuário recusou na equiparação: não voltam a ser
   *  sugeridas para esta carga. */
  viagens_recusadas?: string[] | null
  /** O que está errado nesta viagem, para o triângulo com contador da Frente
   *  (Samuel, 04/09/2026). A primeira anomalia é a divergência de conjunto entre
   *  o Protheus e o HUB. Vazia quando não há viagem equiparada. */
  anomalias?: ShipmentAnomalia[]
  schedule_status: string | null
  /** Farol da programação de carregamento (bolinha da Frente, manual):
   *  null = não enviada, 'enviado', 'recebido'. */
  programacao_status: string | null
  /** Alerta de Observação (Complemento §2): coexiste com trip_status, pinta a linha
   *  de azul. `observation` traz os carimbos de criação/resolução. */
  has_observation?: boolean
  observation?: ShipmentObservation | null
  /** Monitoramento automático ativo. Atendido/Não atendido encerram (Complemento §6). */
  monitoring_active?: boolean
  volume_prev: number | null
  volume_real: number | null
  date: string | null
  shift: 'morning' | 'afternoon' | 'night' | null
  status: ShipmentStatus
  status_label?: string
  harvest_start_prev: string | null
  field_exit_prev: string | null
  arrival_prev: string | null
  harvest_start_real: string | null
  field_exit_real: string | null
  arrival_real: string | null
  unload_end_real: string | null
  released_real: string | null
  eta: string | null
  driver_name: string | null
  notes: string | null
  created_at: string | null
  driver?: PersonRef | null
  truck?: FleetRef | null
  trailer?: FleetRef | null
  /** Subcontrato do Protheus escolhido na linha da Frente. É dele que sai o
   *  Cliente da carga, e com ele a rota e a lavoura da safra. */
  subcontrato?: SubcontratoRef | null
  /** Trocaram uma ponta depois da escala, e a composição resultante não é
   *  conjunto nenhum do cadastro para aquele cavalo.
   *
   *  DERIVADO no servidor, nunca gravado: quando a linha volta a um conjunto
   *  cadastrado, ou a gestão de frota corrige o cadastro, a marca some sozinha. */
  conjunto_alterado?: boolean
  /** A linha da escala é de outra transportadora: ocupa lugar no dia e não tem
   *  conjunto nosso. Pinta a linha de roxo e substitui o motorista na grade e na
   *  imagem da escala. */
  outra_transportadora?: boolean
  /** Conjunto que originou a atribuição, para a linha poder mostrar de onde
   *  a composição saiu. Nulo em carga atribuída ponta a ponta. */
  conjunto_uuid?: string | null
  /** Viagem do ERP equiparada a esta carga pelo de-para por data e placa. É de
   *  onde a grade lê nº e filial da viagem, status e notas. null = sem par. */
  ocorrencia?: OcorrenciaViagemRef | null
}

export type TempoUbsStatus = 'preenchido' | 'pendente' | 'nao_se_aplica'

/** Os dois arquivos que a operação sobe no Protheus a partir do módulo de Viagens. */
export type ViagensArquivoTipo = 'tempo_ubs' | 'apontamento'

/** O que um arquivo levaria agora, sobre o conjunto filtrado. `ja_geradas` não tira
 *  viagem do arquivo: avisa que o espelho ainda não refletiu a importação. */
export interface ViagensArquivo {
  candidatas: number
  ja_geradas: number
  ultima_geracao: string | null
  ultima_geracao_por: string | null
}

/** Linha do Painel de Viagens: desde 23/09/2026, uma VIAGEM DO PROTHEUS, com a
 *  carga do HUB equiparada colada nela quando existe. `uuid` é o da viagem do
 *  espelho; `shipment_uuid` e `number` são da carga, e null quando o HUB não a tem. */
export interface ViagemPainelLinha {
  uuid: string
  shipment_uuid: string | null
  number: string | null
  /** O dia que ordena e recorta pelo período: a Data HUB, e a Data ERP na viagem
   *  que o HUB não registrou (29/10/2026). */
  dia: string | null
  /** A data da carga da Frente, do Transporte de Máquina ou da linha de Outros
   *  Serviços; null sem registro no HUB. */
  data_hub: string | null
  /** A data da viagem no Protheus: a chegada, com a fim e a de início previstas como
   *  recuo. */
  data_erp: string | null
  hora: string | null
  /** O contrato da coluna Frente e contrato: o do HUB a que a viagem se liga, ou, na
   *  viagem vinculada a um Transporte de Máquina, o Contrato Destino dele. null com
   *  `fora_do_hub`. */
  contrato: string | null
  contrato_uuid: string | null
  frente: string | null
  contrato_fonte: ViagemContratoFonte | null
  /** Sem vínculo com o HUB: a coluna diz FORA DO HUB. */
  fora_do_hub: boolean
  /** Sem contrato do HUB, mas numa linha de Outros Serviços Transporte: a coluna diz
   *  OUTROS SERVIÇOS no lugar de FORA DO HUB (Samuel, 08/10/2026). */
  outro_servico?: boolean
  /** Vinculada a um Transporte de Máquina sem contrato e sem local de destino: a
   *  coluna diz TRANSPORTE MÁQUINA no lugar de FORA DO HUB (14/10/2026). */
  transporte_maquina?: boolean
  /** Cancelada no ERP e sem contrato do HUB: a coluna diz CANCELADA no lugar dos três
   *  acima, e o Dashboard não a conta (Samuel, 15/10/2026). */
  cancelada?: boolean
  /** O texto do ERP, lavoura ou contrato, para a dica e a busca. */
  contrato_erp: string | null
  /** O KM Vazio Contrato (01/10/2026): o KM do cadastro do contrato, em
   *  quilômetros, que é o KM vazio esperado da viagem. null fora do HUB, na Prancha
   *  e no contrato sem KM. */
  km_vazio_contrato_km: number | null
  cliente: string | null
  /** O serviço do cadastro de Serviços a que a descrição de serviço da viagem no
   *  ERP foi alocada (Samuel, 24/09/2026). null é Sem serviço: a descrição ainda não
   *  foi alocada, ou a viagem veio sem descrição. */
  servico: string | null
  servico_uuid: string | null
  /** A descrição ainda não foi alocada, ou não veio, e a viagem caiu no serviço
   *  padrão do cadastro (Samuel, 28/09/2026). */
  servico_por_padrao: boolean
  /** O papel do serviço da viagem nas regras do módulo. É por ele que a tela sabe
   *  o que é espiga, em vez de comparar o nome. */
  servico_papel: ServicoPapel | null
  /** O campo Tempo UBS da viagem NO PROTHEUS (29/09/2026): preenchido, pendente, ou
   *  não se aplica para a viagem que não é de espiga. Não é medida do HUB. */
  tempo_ubs_status: TempoUbsStatus
  /** A viagem está em Outros, em Transporte em Geral ou sem serviço, e aceita o
   *  apontamento manual com um dos Outros Serviços (29/09/2026). */
  servico_apontavel: boolean
  /** O serviço interno apontado à mão, mostrado ao lado do original. */
  servico_apontado: string | null
  servico_apontado_uuid: string | null
  /** A descrição de serviço da viagem no ERP, crua, de onde o serviço saiu. */
  tipo_servico: string | null
  /** A dupla registrada na viagem: a do contrato, a mesma do cabeçalho da Frente. */
  dupla: string | null
  motorista: string | null
  /** A base do motorista, sem o nome da empresa. */
  motorista_base: string | null
  placa: string | null
  carreta: string | null
  /** A segunda carreta do rodotrem, que só o ERP conhece (13/10/2026). */
  carreta2: string | null
  /** Status da carga do HUB; null quando a viagem não tem carga no HUB. */
  status: ShipmentStatus | null
  trip_status: string | null
  deviation: string | null
  has_observation: boolean
  destino: string | null
  /** INBOUND ou OUTBOUND, como o ERP classifica a viagem. */
  tipo_operacao: string | null
  /** Status da viagem no ERP, cru como a planilha de viagens o carrega. */
  status_erp: string | null
  /** "filial / número" da viagem do ERP. */
  viagem_erp: string | null
  /** Os botões CT-e e MDF-e, pelo XML que o ERP mandou (21/10/2026). null sem nenhum. */
  documentos?: DocumentosFiscais | null
  /** Tempo das etapas FECHADAS da viagem, em minutos, e a distância GPS dela em
   *  metros. null é "sem medida" — etapa aberta, suspeita ou sem cobertura —,
   *  nunca zero. */
  transito_min: number | null
  descarga_min: number | null
  ciclo_min: number | null
  distancia_m: number | null
  /** Nota Fiscal (Samuel, 08/09/2026): minutos entre a entrada em TRÂNSITO e a
   *  solicitação da NF no ERP, COM SINAL. Negativo ou zero é dentro do prazo, e o
   *  quanto negativo diz de quanta folga; positivo é o atraso. null é "sem medida"
   *  — sem par no ERP, sem nota ou sem entrada em trânsito. Aqui o ZERO É MEDIDA,
   *  ao contrário das quatro acima: é a nota pedida no minuto da saída.
   *  Na tela é a Solicitação de NF desde 08/10/2026. */
  nf_atraso_min: number | null
  /** Emissão NF (Samuel, 08/10/2026): a mesma régua da Solicitação de NF, com a
   *  CONFIRMAÇÃO da solicitação no ERP no lugar da solicitação. Mede o time de
   *  notas; a Solicitação, o de logística. */
  nf_emissao_atraso_min?: number | null
  /** As três medidas de quilômetro, na régua do documento de 22/09/2026.
   *
   *  `km_vazio_m` é a PERNA VAZIA desta viagem: o que o caminhão rodou em
   *  Adiantando, Escalado e Carregando, até entrar em Trânsito. Disponível e
   *  Descarregando não contam. null quando a viagem não passou por nenhum dos três.
   *
   *  `km_carregado_m` é o trânsito, com o nome que a operação usa.
   *
   *  `km_erp` é o KM em QUILÔMETROS que o ERP cobra por esta viagem, do serviço
   *  do subcontrato dela.
   *
   *  A otimização é KM Protheus − KM vazio, sobre os quilômetros inteiros da
   *  linha: `otimizacao_km` em quilômetros e `otimizacao_pct` em percentual do KM
   *  Protheus. null quando falta um dos dois, e a tela escreve Sem otimização.
   *  Negativa é legítima: o caminhão rodou vazio mais do que o Protheus paga. */
  km_vazio_m: number | null
  km_carregado_m: number | null
  km_erp: number | null
  /** `viagem` é o KM Total da própria viagem no ERP; `subcontrato`, a distância do
   *  serviço do subcontrato, que vale enquanto o ERP não preenche o da viagem. */
  km_erp_fonte: 'viagem' | 'subcontrato' | null
  otimizacao_km: number | null
  otimizacao_pct: number | null
  /** A correção pelo lápis (29/09/2026), ou null na viagem que ninguém corrigiu. */
  ajuste: ViagemAjusteResumo | null
}

export type ViagemContratoFonte = 'frente' | 'prancha' | 'garagem'

/** O ajuste do lápis de uma viagem. `suspenso` é o feito sobre outra carga ou outro
 *  ciclo: a linha volta à medida do HUB até alguém salvar de novo. */
export interface ViagemAjusteResumo {
  situacao: 'valido' | 'suspenso'
  /** `transito`, `descarregando`, `disponivel`, `km_vazio` e `km_carregado`. */
  campos: string[]
  por: string | null
  em: string | null
}

export type EtapaDoDetalhe = 'adiantando' | 'escalado' | 'carregando' | 'transito' | 'descarregando' | 'disponivel'

/** O detalhamento que a linha abre ao clicar. `medido` é o que o HUB mediu, e `em`
 *  o que vale na linha: os dois só diferem no campo corrigido pelo lápis. */
export interface ViagemDetalhe {
  viagem: ViagemPainelLinha
  status: { etapa: EtapaDoDetalhe; em: string | null; medido: string | null; editavel: boolean; ajustado: boolean }[]
  descarga: { inicio: string | null; fim: string | null; min: number | null }
  km: {
    vazio_m: number | null
    vazio_medido_m: number | null
    carregado_m: number | null
    carregado_medido_m: number | null
    vazio_contrato_km: number | null
    erp: number | null
    erp_fonte: 'viagem' | 'subcontrato' | null
    otimizacao_km: number | null
    otimizacao_pct: number | null
  }
  /** A Nota Fiscal e a solicitação de onde ela sai, para o lápis mostrar o recálculo
   *  ao corrigir a entrada em Trânsito (01/10/2026). */
  nf: {
    solicitada_em: string | null; atraso_min: number | null; atraso_medido_min: number | null
    /** A ponta da Emissão NF, e a medida dela, como a da Solicitação (08/10/2026). */
    confirmada_em?: string | null; emissao_atraso_min?: number | null; emissao_atraso_medido_min?: number | null
  }
  ajuste: ViagemAjusteResumo | null
  edicoes: { por: string | null; em: string | null }[]
}

/** O corpo do lápis: campo ausente fica como está, nulo volta ao medido. Instante em
 *  ISO com fuso; quilômetro em quilômetros. */
export interface ViagemAjusteCorpo {
  transito_em?: string | null
  descarregando_em?: string | null
  disponivel_em?: string | null
  km_vazio_km?: number | null
  km_carregado_km?: number | null
}

/** Uma linha da tabela de placas ou de motoristas do Dashboard. Sem KM cheio desde
 *  01/10/2026; `servicos` conta as viagens por serviço final, Pneu e não Outros. */
export interface ViagensTabelaGrupo {
  viagens: number
  servicos: Record<string, number>
  km_vazio_m: number | null
  km_protheus: number | null
  otimizacao_km: number | null
  otimizacao_pct: number | null
  peso_t: number | null
  motoristas: number
}

export interface ViagensOtimizacao {
  viagens: number
  medidas: number
  km_protheus: number | null
  km_vazio_m: number | null
  otimizacao_km: number | null
  otimizacao_pct: number | null
}

/** Os indicadores do topo do Dashboard (01/10/2026). KM e peso só de Espiga. */
export interface ViagensIndicadores {
  viagens: number
  km_total_espiga: number | null
  km_medio_espiga: number | null
  km_viagens_espiga: number
  peso_total_t: number | null
  peso_medio_t: number | null
  peso_viagens: number
  /** Pela régua do alerta: até `prazo_min` depois da entrada em Trânsito é dentro do
   *  prazo. O atraso médio é o das que passaram disso. */
  nf: ViagensDesempenhoNf
  /** Emissão NF, o time de notas, na mesma régua (Samuel, 08/10/2026). A `nf` acima
   *  é a Solicitação de NF, o time de logística. */
  nf_emissao?: ViagensDesempenhoNf
}

export interface ViagensDesempenhoNf {
  medidas: number
  fora: number
  prazo_min: number
  dentro_pct: number | null
  fora_pct: number | null
  atraso_medio_min: number | null
}

/** A aba Dashboard do módulo (29/09/2026), sobre o conjunto filtrado. */
export interface ViagensDashboard {
  viagens: number
  viagens_espiga: number
  indicadores: ViagensIndicadores
  /** O mini ranking ao lado do gráfico de Espiga por dia: motivo, cargas e o
   *  percentual sobre as não atendidas. */
  motivos_nao_atendimento: { total: number; itens: { motivo: string; cargas: number; pct: number }[] }
  /** As colunas de serviço das tabelas de placas e motoristas, em ordem. */
  servicos: string[]
  por_cliente: {
    nome: string
    viagens: number
    km_total: number | null
    km_medio: number | null
    km_viagens: number
    peso_t: number | null
    peso_medio_t: number | null
    peso_viagens: number
  }[]
  por_destino_espiga: { nome: string; viagens: number; descarga_media_min: number | null; descarga_viagens: number }[]
  por_dia_espiga: { dia: string; total: number; atendidas: number; em_andamento: number; nao_atendidas: number }[]
  otimizacao_espiga: ViagensOtimizacao
  por_dupla: { nome: string; viagens: number }[]
  por_destino: { nome: string; viagens: number }[]
  por_placa: (ViagensTabelaGrupo & { placa: string; modalidade: string | null })[]
  por_motorista: (ViagensTabelaGrupo & { motorista: string; base: string | null })[]
}

/** As cinco séries do Dashboard de Colheita (05/10/2026), contadas em CARGAS da
 *  programação de colheita do HUB, pela régua da Frente. */
export interface ColheitaSeries {
  solicitadas: number
  atendidas: number
  nao_atendidas: number
  /** Programação que já passou sem lançamento. */
  pendentes: number
  /** Programação de hoje ou de depois, ainda sem lançamento. */
  em_andamento: number
}

/** Os números da colheita, a página Números Colheita do Power BI do Samuel, sobre as
 *  medições do Protheus. `dias_medio` é a média, por contrato, do primeiro ao último
 *  dia medido, contando os dois; null quando não há contrato no recorte. */
export interface ColheitaNumeros {
  indicadores: { hectares: number; maquinas: number; contratos: number; medicoes: number; dias_medio: number | null }
  /** `nome` null é a medição sem cliente. */
  por_cliente: { nome: string | null; hectares: number; maquinas: number; contratos: number; dias_medio: number | null }[]
  por_maquina: { maquina: string; tipo: string | null; hectares: number; contratos: number; operadores: number; medicoes: number }[]
  /** `operador` null é a medição sem operador. */
  por_operador: { operador: string | null; hectares: number; maquinas: number; contratos: number; medicoes: number }[]
  /** Quando o ciclo do Protheus confirmou as medições pela última vez. */
  atualizado_em: string | null
}

/** Situação de uma programação de colheita, a régua da Frente: `lancada` tem o
 *  atendido, `pendente` passou do dia sem ele, `andamento` é de hoje ou depois. */
export type ColheitaSituacao = 'lancada' | 'pendente' | 'andamento'

/** Uma linha da tabela por operador (Samuel, 27/10/2026): uma pessoa da equipe numa
 *  programação de colheita. As cargas são da programação INTEIRA, que a Frente lança
 *  para a equipe; `equipe` diz quantas máquinas dividem esse número. */
export interface ColheitaLinhaProgramacao {
  chave: string
  dia: string
  hora: string | null
  programacao_uuid: string
  contrato: string | null
  contrato_uuid: string | null
  cliente: string | null
  /** Null só na programação sem equipe. */
  operador: string | null
  maquina: string | null
  equipe: number
  situacao: ColheitaSituacao
  solicitadas: number
  /** Null enquanto a Frente não lança: zero é lançamento. */
  atendidas: number | null
  nao_atendidas: number | null
}

/** Onde uma máquina está agora (Samuel, 28/10/2026): a última viagem concluída dela
 *  no Transporte de Máquina, pela Data Fim. `cliente` é nulo quando o destino é
 *  garagem. Sem o recorte do período. */
export interface ColheitaLocalizacao {
  maquina_uuid: string
  placa: string | null
  local: string | null
  garagem: boolean
  data: string | null
  cliente: string | null
  /** O registro da viagem no HUB, como TM-000123. */
  viagem: string | null
  /** A máquina foi rodando, sem prancha. */
  rodando: boolean
}

export interface ColheitaDashboard {
  medicoes: ColheitaNumeros
  programacao: {
    indicadores: ColheitaSeries & { programacoes: number }
    /** Todos os dias do período, inclusive os sem programação. */
    por_dia: (ColheitaSeries & { dia: string })[]
    /** A mais recente primeiro. */
    linhas: ColheitaLinhaProgramacao[]
  }
  /** Em ordem de placa, só das máquinas com viagem. */
  localizacao: ColheitaLocalizacao[]
}

/** As tabelas do Dashboard que saem em Excel (28/10/2026). */
export type ColheitaTabelaExcel = 'maquina' | 'operador' | 'localizacao' | 'programacao'

/** As opções dos seletores de Máquina e de Operador do módulo: a chave do casamento
 *  e o rótulo mais completo que apareceu no período. */
export interface ColheitaEquipe {
  maquinas: { valor: string; rotulo: string }[]
  operadores: { valor: string; rotulo: string }[]
}

/** Uma medição de campo do Protheus, a linha da aba Colheitas do Protheus. Sem
 *  nenhum dado de faturamento: só a medição entra no HUB. */
export interface ColheitaMedicao {
  uuid: string
  medicao: string
  data_inicial: string | null
  data_final: string | null
  data_lancamento: string | null
  contrato: string | null
  contrato_uuid: string | null
  fora_do_hub: boolean
  pivot: string | null
  cliente: string | null
  produtor: string | null
  produto: string | null
  tipo_maquina: string | null
  maquina: string | null
  operador: string | null
  hectares: number | null
  dupla: string | null
}

/** Um alerta do Relatório de Alertas da Colheita (13/10/2026): a medição do
 *  Protheus com a janela da programação da Frente ao lado. Desde 27/10/2026, também
 *  os hectares contra o Maquinário e a medição sem frente no HUB. Desde 29/10/2026
 *  o início e o fim diferentes são um alerta só, `data_diverge`, e `fora_do_local`
 *  é o da máquina, que vem em `maquinas`. */
export type ColheitaAlertaCodigo = 'sem_programacao' | 'data_diverge' | 'hectares_diverge' | 'sem_frente' | 'fora_do_local'
export interface ColheitaAlerta extends ColheitaMedicao {
  /** A situação do contrato, a da barra de contratos da Frente (28/10/2026). Nula na
   *  medição FORA DO HUB. */
  situacao_frente: ContractSituacao | null
  /** A primeira e a última programação de colheita do operador no contrato. */
  hub_inicio: string | null
  hub_fim: string | null
  hub_programacoes: number
  casado_por: 'operador' | 'maquina' | null
  /** A soma das medições que caem nas mesmas linhas do Maquinário e a soma da Q.
   *  Hectares dessas linhas. Nulos quando a medição não tem linha no Maquinário. */
  hectares_protheus: number | null
  hectares_frente: number | null
  medicoes_somadas: number
  linhas_somadas: number
  alertas: ColheitaAlertaCodigo[]
}
/** A máquina longe do local da última viagem (29/10/2026): a linha da Localização
 *  atual da máquina com a última posição do rastreador e a distância até o centro
 *  do local. */
export interface ColheitaMaquinaForaDoLocal extends ColheitaLocalizacao {
  raio_km: number
  distancia_km: number
  posicao_em: string
  posicao_lat: number
  posicao_lng: number
  posicao_endereco: string | null
  rastreador: 'ssx' | 'truckscontrol' | 'aldeia'
}
export interface ColheitaAlertas {
  itens: ColheitaAlerta[]
  /** A mais longe primeiro. Vazia quando o filtro de alerta não pede `fora_do_local`. */
  maquinas: ColheitaMaquinaForaDoLocal[]
  /** As medições e as máquinas com alerta. */
  total: number
  /** Quantas medições foram comparadas, com ou sem alerta. */
  conferidas: number
  indicadores: Record<ColheitaAlertaCodigo, number>
}

export interface ColheitaMedicoes {
  itens: ColheitaMedicao[]
  total: number
  hectares: number
  maquinas: number
}

export type ViagemAlertaTipo =
  | 'otimizacao' | 'descarga_curta' | 'descarga_longa' | 'transito_longo' | 'transito_curto' | 'km_baixo'
  | 'nf_atrasada'
export type ViagemAlertaStatus = 'pendente' | 'corrigido' | 'justificado'

/** As colunas da linha que o retrato do alerta congela na detecção (30/09/2026). */
export interface ViagemAlertaMedidas {
  otimizacao_km: number | null
  otimizacao_pct: number | null
  km_vazio_m: number | null
  km_erp: number | null
  transito_min: number | null
  descarga_min: number | null
  nf_atraso_min?: number | null
  km_vazio_contrato_km?: number | null
}

/** Uma linha do Relatório de Alertas: o alerta e a viagem dele, AO VIVO. */
export interface ViagemAlertaLinha {
  uuid: string
  tipo: ViagemAlertaTipo
  status: ViagemAlertaStatus
  /** O valor que passou do limite e o limite, na detecção, e as colunas da linha
   *  naquele instante, que são o "antes" do Corrigido. `medidas` falta no alerta de
   *  antes de 30/09/2026 que fechou sem passar por outra conciliação. */
  retrato: { valor: number; limite: number; medidas?: ViagemAlertaMedidas } | null
  /** A faixa de trânsito de agora, com o KM de agora. */
  /** `do_cliente` só na descarga acima do limite: o teto é o do cliente da viagem,
   *  da aba Tempo de UBS, e não as 24 horas gerais (05/10/2026). */
  limite_atual: { minimo_min: number; maximo_min: number; do_cliente?: boolean } | null
  detectado_em: string | null
  corrigido_em: string | null
  corrigido_motivo: 'ajuste' | 'medida' | null
  justificativa: string | null
  justificado_por: string | null
  justificado_em: string | null
  filial: string
  viagem: Pick<ViagemPainelLinha,
    'uuid' | 'dia' | 'viagem_erp' | 'contrato' | 'contrato_fonte' | 'fora_do_hub' | 'outro_servico'
    | 'transporte_maquina' | 'cancelada' | 'contrato_erp' | 'frente'
    | 'servico' | 'servico_papel' | 'servico_apontado' | 'tipo_servico' | 'dupla' | 'status_erp' | 'placa'
    | 'motorista' | 'trip_status' | 'otimizacao_km' | 'otimizacao_pct' | 'transito_min' | 'descarga_min'
    | 'km_erp' | 'km_erp_fonte' | 'km_vazio_m' | 'ajuste' | 'nf_atraso_min'
    | 'km_vazio_contrato_km'>
}

export interface ViagensAlertas {
  itens: ViagemAlertaLinha[]
  total: number
  page: number
  pages: number
  per_page: number
  /** Por status, sobre o recorte sem o filtro de status. */
  indicadores: Record<ViagemAlertaStatus, number>
}

/** O status de cobrança da diária na aba Tempo de UBS (05/10/2026). */
export type CobrancaStatus = 'nao_cobrado' | 'cobrado'

/** Uma linha da aba Tempo de UBS: a viagem cuja descarga passou do limite do
 *  cliente. Os três tempos em minutos; as duas pontas da descarga já com o lápis. */
export interface ViagemTempoUbsLinha {
  uuid: string
  dia: string | null
  cliente: string | null
  contrato: string | null
  fora_do_hub: boolean
  outro_servico?: boolean
  transporte_maquina?: boolean
  cancelada?: boolean
  contrato_erp: string | null
  /** Filial e número da viagem no Protheus, como `0101 / 089100`. */
  viagem_erp: string | null
  /** O SML da carga, digitado na linha da Frente; null sem carga ou em branco. */
  sml: string | null
  motorista: string | null
  placa: string | null
  carreta: string | null
  inicio_descarga: string | null
  disponivel_em: string | null
  descarga_min: number
  limite_min: number
  excedente_min: number
  status_cobranca: CobrancaStatus
  alterado_em: string | null
  alterado_por: string | null
  /** Toda mudança de status, da mais recente para a mais antiga. */
  historico: { status: CobrancaStatus; em: string | null; por: string | null }[]
}

export interface ViagensTempoUbs {
  itens: ViagemTempoUbsLinha[]
  total: number
  page: number
  pages: number
  per_page: number
  /** Por status, sobre o recorte sem o filtro de status. */
  indicadores: Record<CobrancaStatus, number>
}

/** A situação da cobrança do não atendimento, lida dos dois status: Recebido
 *  implica Cobrado. */
export type CobrancaNaoAtendimentoSituacao = 'a_cobrar' | 'cobrado' | 'recebido'

/** O que a aba Cobrança Não atendimento grava na carga (29/10/2026). */
export interface CobrancaNaoAtendimentoEstado {
  valor_diaria: number | null
  valor_alterado_em: string | null
  valor_alterado_por: string | null
  cobrado: boolean
  cobrado_em: string | null
  cobrado_por: string | null
  recebido: boolean
  recebido_em: string | null
  recebido_por: string | null
  situacao: CobrancaNaoAtendimentoSituacao
}

/** Uma linha da aba Cobrança Não atendimento: a carga da Frente encerrada como Não
 *  atendido com a Cobrança Sim. `uuid` é o da carga, e `dia`, a data dela. */
export interface CobrancaNaoAtendimentoLinha extends CobrancaNaoAtendimentoEstado {
  uuid: string
  dia: string | null
  number: string | null
  cliente: string | null
  contrato: string | null
  frente: string | null
  motorista: string | null
  placa: string | null
  carreta: string | null
  motivo: string | null
  /** O que será cobrado, como foi escrito no encerramento. */
  observacao: string | null
  encerrado_em: string | null
  encerrado_por: string | null
}

export interface CobrancaNaoAtendimento {
  itens: CobrancaNaoAtendimentoLinha[]
  total: number
  page: number
  pages: number
  per_page: number
  /** Por situação, sobre o recorte sem o filtro de situação: cargas e soma das diárias. */
  indicadores: Record<CobrancaNaoAtendimentoSituacao, { cargas: number; valor: number }>
}

/** O tempo limite de descarga de um cliente, em minutos; null é sem limite. */
export interface LimiteDescargaItem {
  uuid: string
  nome: string
  limite_min: number | null
}

/** Distribuição do tempo de uma etapa sobre as viagens do filtro: só as
 *  fechadas entram nos números; `abertas` conta quem está nela agora. */
export interface ViagensResumoEtapa extends ResumoTempos {
  abertas: number
}

/** Cruzamento por placa ou por motorista. `parceiros` é quantos do outro lado
 *  passaram pelo grupo: motoristas pela placa, placas pelo motorista. */
export interface ViagensGrupo {
  nome: string
  viagens: number
  atendidas: number
  nao_atendidas: number
  em_andamento: number
  /** Soma do que foi medido; null quando nenhuma viagem do grupo tem medida. */
  distancia_m: number | null
  medidas: number
  transito_med_min: number | null
  descarga_med_min: number | null
  ciclo_med_min: number | null
  parceiros: number
}

/** Um ponto da curva do dia: quantas viagens em cada status no minuto `t`. */
export interface ViagensPontoCurva {
  t: number
  escalado: number
  carregando: number
  transito: number
  descarregando: number
  atendido: number
  nao_atendido: number
}

/** Cliente com viagem no período, para o seletor de Cliente do Painel de Viagens. */
export interface ViagensCliente {
  uuid: string
  nome: string
}

/** Uma descrição de serviço do ERP, como o ERP a escreve, e quantas viagens do
 *  espelho a carregam. */
export interface ServicoDescricao {
  descricao: string
  viagens: number
}

/** O lugar do serviço nas regras do módulo de Viagens (29/09/2026). O documento
 *  "Reestruturação do Módulo de Viagens" escreve regra por serviço, "somente Espiga"
 *  e "não se aplica ao serviço Diária", e o nome do serviço é da operação. É o papel,
 *  e nunca o nome, que diz qual serviço é o de espiga. */
export type ServicoPapel = 'espiga' | 'prancha' | 'diaria' | 'outros'

/** Um serviço do cadastro de Serviços (Samuel, 24/09/2026): o nome que a operação
 *  dá a um grupo de descrições de serviço do ERP, como Prancha. */
export interface Servico {
  uuid: string
  nome: string
  /** null é o serviço que não entra em regra nenhuma. */
  papel: ServicoPapel | null
  /** Um dos Outros Serviços do HUB (29/09/2026): não recebe descrição do ERP, não é
   *  o padrão e não tem papel. Serve ao apontamento manual da viagem. */
  interno: boolean
  /** Recebe toda viagem sem serviço, a sem descrição e a de descrição não alocada
   *  (Samuel, 28/09/2026). No máximo um serviço é o padrão. */
  padrao: boolean
  descricoes: ServicoDescricao[]
  /** Soma das viagens das descrições do serviço. */
  viagens: number
  created_at: string | null
  updated_at: string | null
}

/** Toda descrição que o ERP já mandou, com o serviço a que pertence. As grafias da
 *  mesma descrição chegam somadas numa linha só. */
export interface ServicoDescricaoDisponivel extends ServicoDescricao {
  servico: { uuid: string; nome: string } | null
}

export interface ServicoDescricoes {
  itens: ServicoDescricaoDisponivel[]
  /** O que falta alocar. `viagens_sem_servico` soma a descrição livre e a viagem
   *  que veio sem descrição, as duas que o Painel de Viagens mostra no serviço
   *  padrão, ou como Sem serviço quando não há padrão. */
  resumo: {
    servicos: number
    /** Os Outros Serviços do HUB, contados à parte: não recebem descrição do ERP. */
    servicos_internos: number
    descricoes: number
    sem_servico: number
    viagens_sem_servico: number
    viagens_sem_descricao: number
    /** O serviço que recebe a sobra (28/09/2026), ou null quando nenhum é o padrão. */
    padrao: { uuid: string; nome: string } | null
  }
}

/** Um cliente do cadastro, vindo do Protheus, como a grade de Clientes o nomeia: o
 *  fantasia, e a razão social na falta dele. */
export interface ClienteHubCliente {
  uuid: string
  nome: string
  empresa_pai: string | null
}

/** Um cliente do HUB (Samuel, 08/10/2026): o nome que a operação dá a um grupo de
 *  clientes do Protheus, como Corteva para CORTEVA FORMOSA e PIONEER FORMOSA. */
export interface ClienteHub {
  uuid: string
  nome: string
  clientes: ClienteHubCliente[]
  created_at: string | null
  updated_at: string | null
}

/** Todo cliente do cadastro, com o cliente HUB a que pertence, e o que falta vincular. */
export interface ClientesHubDisponiveis {
  itens: Array<ClienteHubCliente & { cliente_hub: { uuid: string; nome: string } | null }>
  resumo: { clientes_hub: number; clientes: number; sem_cliente_hub: number }
}

/** Uma linha de Outros Serviços Transporte ativa no dia, para o Resumo de Outros
 *  Serviços do Dashboard (08/10/2026). A linha vale em todo dia entre a Data de
 *  início e a de fim, e até hoje quando o fim está em branco. */
export interface ResumoOutroServicoItem {
  uuid: string
  dupla_uuid: string | null
  /** O cliente do cadastro da viagem vinculada, para o filtro Cliente do Dashboard. */
  customer_uuid: string | null
  /** A linha tem viagem do Protheus vinculada. Sem ela, o cliente é desconhecido. */
  vinculada: boolean
  cliente_hub: { uuid: string; nome: string } | null
  /** O nome do cliente na viagem do Protheus, para o cliente ainda sem Cliente HUB. */
  cliente_protheus: string | null
  /** O Serviço HUB da linha. Nulo é o serviço ainda não classificado. */
  servico: string | null
  diaria: boolean
  /** Os valores dos filtros Cliente HUB e Serviço HUB da grade que trazem esta linha,
   *  para o atalho do Resumo abrir Outros Serviços Transporte já filtrado. */
  filtro: { cliente: string; servico: string }
}

export interface ResumoOutrosServicos {
  data: string
  itens: ResumoOutroServicoItem[]
}

export interface ViagensPainel {
  arquivos: Record<ViagensArquivoTipo, ViagensArquivo>
  kpis: {
    total: number
    atendidas: number
    nao_atendidas: number
    em_andamento: number
    sem_status: number
    desvios: number
    /** Viagens do ERP sem carga equiparada no HUB: ficam sem medida nenhuma. */
    sem_vinculo_hub: number
    /** Os cruzamentos: soma do quilômetro MEDIDO e médias das etapas fechadas.
     *  null é "nada medido", nunca zero. */
    distancia_m: number | null
    transito_medio_min: number | null
    descarga_media_min: number | null
    ciclo_medio_min: number | null
    /** Percentual das viagens MEDIDAS cuja NF foi pedida até a saída da fazenda,
     *  e o tamanho da amostra. A amostra é pequena por natureza: só carga com par
     *  no ERP e com nota entra nela. null é "nada medido" — zero por cento diria
     *  que toda nota atrasou. */
    nf_no_prazo_pct: number | null
    nf_medidas: number
    /** Otimização do PERÍODO: a fórmula sobre os totais, não a média das
     *  otimizações por viagem, senão uma viagem de 8 km pesaria como uma de 800.
     *  Só as viagens com KM Protheus e KM vazio entram, e `otimizacao_medidas` diz
     *  quantas são. `km_protheus` já vem em quilômetros; `km_vazio_m`, em metros. */
    otimizacao_pct: number | null
    otimizacao_km: number | null
    otimizacao_medidas: number
    km_vazio_m: number | null
    km_protheus: number | null
    placas: number
    motoristas: number
  }
  itens: ViagemPainelLinha[]
  /** Linhas da GRADE. Carga sem motorista e sem placa fica fora dela, mas segue
   *  nos KPIs e nos gráficos, porque é demanda não atendida (07/09/2026). */
  total: number
  ocultas_sem_composicao: number
  page: number
  pages: number
  per_page: number
  analiticos: {
    por_status_hub: Record<string, number>
    por_status_erp: Record<string, number>
    por_dia: {
      dia: string
      total: number
      atendidas: number
      nao_atendidas: number
      /** Escalado a Descarregando, a régua do indicador Em andamento (28/09/2026). */
      em_andamento: number
      /** Sem status do HUB: a viagem do Protheus sem carga no HUB. */
      sem_status: number
      distancia_m: number | null
      medidas: number
    }[]
    ranking_duplas: { nome: string; viagens: number }[]
    ranking_motoristas: { nome: string; viagens: number }[]
    ranking_destinos: { nome: string; viagens: number }[]
    /** Tempo por etapa da máquina e do ciclo inteiro, da escala ao Atendido. */
    etapas: {
      escalado: ViagensResumoEtapa
      carregando: ViagensResumoEtapa
      transito: ViagensResumoEtapa
      descarregando: ViagensResumoEtapa
      ciclo: ViagensResumoEtapa
      sem_evento: number
    }
    distancia: {
      /** O quilômetro das viagens, que é o do Trânsito: Descarregando não conta
       *  quilômetro desde 22/09/2026. */
      total_m: number | null
      transito_m: number | null
      /** Soma da perna vazia das viagens do filtro. Fora de `total_m` de
       *  propósito: o total é o quilômetro DA viagem. */
      vazio_m: number | null
      medidas: number
      /** Viagens fechadas que ainda esperam o congelamento da medida. */
      pendentes: number
      sem_veiculo: number
      ao_vivo: number
    }
    /** Transições por hora do dia, no relógio da operação. */
    por_hora: { hora: number; saidas: number; chegadas: number; mudancas: number }[]
    /** A curva do dia, só quando o período É um dia. */
    composicao: {
      agora_min: number
      passo: number
      pontos: ViagensPontoCurva[]
      sem_evento: number
    } | null
    por_placa: ViagensGrupo[]
    por_motorista: ViagensGrupo[]
  }
}

/** Nota da viagem do ERP, como a origem entrega dentro de `notas`. */
export interface NotaViagemErp {
  lote_solicitacao: string | null
  chave_nfe: string | null
  /** Legenda Solicitação Documentação. A origem manda ora o código — 1, 2, 3, 9
   *  ou C —, ora a palavra: "Confirmado". `protheusNfDot` cobre os dois. */
  status_solicitacao: string | null
  /** Quando a NF foi solicitada, publicado em 08/09/2026. Data em AAAAMMDD e hora
   *  em HH:MM, PAREDE LOCAL. É o par que sustenta a medida Nota Fiscal do Painel
   *  de Viagens, e quem os interpreta é o servidor. */
  data_solicitacao: string | null
  hora_solicitacao: string | null
}

/** Recorte da ocorrência de viagem do Protheus embutido na carga equiparada. */
export interface OcorrenciaViagemRef {
  uuid: string
  item: string
  data_agendamento: string | null
  filial_viagem: string | null
  viagem: string | null
  status_viagem: string | null
  /** A viagem TEM pedágio? Ver `FrenteOcorrencia.pedagio`. */
  pedagio: boolean | null
  /** Estado da informação do vale pedágio no ERP. É ele que acende a coluna
   *  Status VP da grade da Frente desde 08/09/2026; o estado do HUB fica como
   *  recuo, para a carga que o ERP ainda não descreve. */
  status_pedagio: string | null
  notas: NotaViagemErp[]
  /** Os botões CT-e e MDF-e da carga, pelas listas do getfrentes (21/10/2026). */
  documentos?: DocumentosFiscais | null
}

/** O que os botões CT-e e MDF-e precisam para abrir o DACTE e o DAMDFE (21/10/2026):
 *  o par da viagem no ERP, que é a chave da rota, e quais dos dois documentos ela tem.
 *  O servidor manda null quando não há nenhum. */
export interface DocumentosFiscais {
  filial: string
  viagem: string
  cte: boolean
  mdfe: boolean
}

/** Subcontrato do Protheus — espelho somente leitura do `getSubcontratos`.
 *
 *  O subcontrato é o recorte da safra que amarra contrato, rota, lavoura e a dupla
 *  que programa. Nenhuma tela edita: a sincronização reescreve a cada ciclo. */
export interface Subcontrato {
  uuid: string
  /** Chave na origem, ex. "V-26/2071.0000 TC". */
  subcontrato: string
  /** Contrato-mãe no Protheus. NÃO casa com o número de contrato do HUB. */
  contrato: string | null
  descricao: string | null
  /** Quem paga o frete. É esta a coluna Cliente do Painel (decidido 19/08/2026). */
  cliente: string | null
  devedor: string | null
  remetente: string | null
  remetente_documento: string | null
  remetente_inscricao_estadual: string | null
  destinatario: string | null
  /** Nome fantasia das três pontas, publicado em 28/08/2026 a pedido nosso.
   *  É como a operação chama a empresa: "SYNGENTA FORMOSA" no lugar de
   *  "SYNGENTA SEEDS LTDA". Difere da razão social em 2867 de 2943 registros, e
   *  é o único nome que o `getfrentes` publica — guardar os dois é o que permite
   *  reconciliar as duas fontes sem casar por texto. */
  cliente_fantasia: string | null
  devedor_fantasia: string | null
  remetente_fantasia: string | null
  destinatario_fantasia: string | null
  /** Município das duas pontas do frete, "CRISTALINA-GO". Cidade e UF chegam
   *  grudadas, e o servidor guarda como veio: nenhum de para de município foi
   *  acordado com a origem. */
  remetente_municipio: string | null
  destinatario_municipio: string | null
  /** Status cru da origem. CANCELADO e ENCERRADO ficam fora da operação do dia. */
  status: string | null
  vigente: boolean
  tipo_documento: string | null
  safra: string | null
  ano: string | null
  campo: string | null
  rota: string | null
  lavoura: string | null
  dupla_codigo: string | null
  dupla: string | null
  /** Ampliação de 26/08/2026. Peso em quilos, pedágio em reais. O servidor já
   *  converte: a origem manda em pt-BR, "467.000,0000". */
  peso_estimado: number | null
  peso_executado: number | null
  peso_cobranca: number | null
  nr_viagens: number | null
  /** SIM ou NAO na origem. `null` quando ela mandar uma terceira coisa. */
  regime_especial: boolean | null
  filial_origem: string | null
  valor_pedagio: number | null
  /** Par convertido, em grau decimal. `null` quando o texto de origem não
   *  converte ou cai fora da caixa plausível da operação. */
  latitude: number | null
  longitude: number | null
  /** O texto como a origem mandou. Existe para a tela poder distinguir cadastro
   *  vazio de cadastro com valor ilegível: 1913 dos 3093 chegam vazios e nove
   *  chegam com lixo, entre eles `vc`. */
  latitude_origem: string | null
  longitude_origem: string | null
  /** Serviços contratados, como a origem os entrega. `distancia` vem em pt-BR e
   *  não é quilometragem de rota confiável: em 2737 dos 5215 itens vale "1,0". */
  servicos: SubcontratoServico[]
  synced_at: string | null
}

/** Um serviço dentro do subcontrato. 16 códigos distintos na carga de 26/08/2026. */
export interface SubcontratoServico {
  codigo_servico: string
  descricao_servico: string
  distancia: string
}

/** Cabeçalho da grade de Subcontratos: último ciclo e contagem por status cru.
 *  A data vem da própria tabela, porque essa sincronização não grava lote. */
export interface SubcontratoResumo {
  synced_at: string | null
  total: number
  vigentes: number
  por_status: { status: string | null; total: number }[]
}

/** Planilha de campo do Protheus, espelho do `getplanilhas`.
 *
 *  Par do `Subcontrato`: onde aquele é o recorte de safra do TRANSPORTE, com
 *  contrato `.TR`, este é o do CAMPO, com `.COL` colheita, `.DES` despendoamento
 *  e `.ALG` aluguel. Os contratos-mãe dos dois lados são disjuntos.
 *
 *  Não tem `vigente`, e a ausência é decisão: o `status` da planilha descreve
 *  andamento comercial, não operação. Das 45 frentes vivas de 27/08/2026, 37
 *  apontavam para planilha `Encerrado`. */
export interface Planilha {
  uuid: string
  /** As duas pontas da chave. O número da planilha recomeça a cada contrato. */
  contrato: string
  planilha: string
  /** Número que liga campo, transporte e frente. Casou 46 de 46 no trilho GCT em
   *  27/08/2026. Vem SEM o sufixo de modalidade que o lado do transporte carrega,
   *  então comparar com `Subcontrato.subcontrato` exige recortar o sufixo. E nem
   *  assim vira chave: ele repete entre contratos. */
  subcontrato: string | null
  /** Sufixo do contrato, cru: `COL`, `DES`, `ALG`. Calculado no servidor. */
  tipo: string | null
  filial: string | null
  safra: string | null
  ano: string | null
  /** Pivô ou talhão onde o serviço acontece, "M040/25". */
  pivot: string | null
  /** Quem CONTRATA o serviço, razão social e fantasia. 35 distintos. */
  nome_cliente: string | null
  fantasia_cliente: string | null
  /** O PRODUTOR onde o serviço acontece. Não é o cliente: 446 distintos. */
  nome_cooperante: string | null
  fantasia_cooperante: string | null
  /** AAAA-MM-DD. As duas realizadas chegam nulas em 177 de 1771. */
  inicio_previsto: string | null
  final_previsto: string | null
  data_inicio: string | null
  data_final: string | null
  /** Medição do trabalho de campo. A origem ainda não preenche nenhuma. */
  qtd_contratado: number | null
  qtd_realizado: number | null
  /** Como a origem grava, em Title Case. Não é normalizado. */
  status: string | null
  data_lancamento: string | null
  synced_at: string | null
}

/** Cabeçalho da grade de Planilhas: último ciclo e contagem por status cru.
 *  `com_medicao` no lugar de `vigentes`, porque é a pergunta que está aberta com
 *  a equipe Protheus e não há vigência a contar aqui. */
export interface PlanilhaResumo {
  synced_at: string | null
  total: number
  com_medicao: number
  por_status: { status: string | null; total: number }[]
}

/** Frente de trabalho do Protheus, espelho do `getfrentes`.
 *
 *  É a peça que AMARRA os dois lados da safra. `Subcontrato` é o recorte de
 *  transporte e `Planilha` o de campo, e o número do subcontrato é o vocabulário
 *  comum aos dois — só que ele não identifica: dos 1186 presentes nos dois lados,
 *  438 são ambíguos. A frente nomeia o par exato de cada trilho, e resolve 18 de 18
 *  e 46 de 46.
 *
 *  O limite é o que decide a expectativa da tela: ela alcança 46 das 1628 planilhas
 *  e 18 dos 2943 subcontratos. Caminho seguro, e estreito.
 *
 *  Não tem `vigente`, como a planilha e ao contrário do subcontrato: a lista de
 *  `status_frente` ainda vai mudar de nome pela própria origem, e a frente
 *  finalizada não vem na carga. */
/** O lado da safra que uma frente do ERP trouxe ao contrato do HUB. */
export type FrenteTrilho = 'tms' | 'gct' | 'ambos' | 'nenhum'

/** Uma frente do ERP vinculada ao contrato do HUB. */
export interface ContractFrenteVinculo {
  num_frente: string
  trilho: FrenteTrilho
  vinculada_em: string | null
  /** O status da frente no Protheus, como o ERP manda, e o rótulo com acento
   *  (29/10/2026). Nulos quando a frente não está no espelho. */
  status?: string | null
  status_label?: string | null
}

/** A frente de trabalho do HUB que já reivindica uma frente do ERP. */
export interface FrenteContratoHub {
  uuid: string
  contract_number: string | null
  name: string
  trilho: FrenteTrilho
}

export interface Frente {
  uuid: string
  /** A chave, e identifica SOZINHA: 61 distintos em 61. Diferente do subcontrato e
   *  da planilha, que precisam do par com o contrato-mãe. */
  num_frente: string
  /** Em Conferencia, Conferido, Em Execucao, Previsto. Caixa mista sem acento, um
   *  terceiro estilo além do caixa alta do subcontrato e do Title Case da planilha. */
  status_frente: string | null
  /** Rótulo da origem, com o cliente cortado em 15 caracteres. NÃO identifica o
   *  cliente: mesmo defeito de largura do município do getOperadores. */
  apelido: string | null
  codigo_dupla: string | null
  /** Trilho de TRANSPORTE. `devedor_tms` é NOME FANTASIA, e é por isso que ele casa
   *  com `Subcontrato.cliente_fantasia`, nunca com `cliente`. Os quatro chegam
   *  vazios quando a frente não tem este lado. */
  filial_tms: string | null
  devedor_tms: string | null
  contrato_tms: string | null
  subcontrato_tms: string | null
  /** Trilho de CAMPO. Resolve em `Planilha` pelo par contrato mais subcontrato. */
  filial_gct: string | null
  devedor_gct: string | null
  contrato_gct: string | null
  subcontrato_gct: string | null
  /** Calculados no servidor de propósito: a pergunta "quais lados esta frente tem"
   *  é a mesma em toda tela, e derivá-la de quatro campos anuláveis daria respostas
   *  diferentes em cada uma. */
  tem_transporte: boolean
  tem_campo: boolean
  /** Só no detalhe. São 170 para 61 frentes, e mandá-las na listagem triplicaria a
   *  carga da grade para entregar o que só o diálogo mostra. */
  ocorrencias?: FrenteOcorrencia[]
  synced_at: string | null
  /** Qual frente de trabalho do HUB já reivindica esta frente. Duas irmãs mostram
   *  o MESMO contrato: é assim que a grade diz que são tratadas juntas. */
  contrato_hub?: FrenteContratoHub | null
}

/** Uma linha da linha do tempo da frente. Ocorrência aqui não é incidente. */
export interface FrenteOcorrencia {
  uuid: string
  /** A frente dona da linha. Importa quando a lista une mais de uma frente. */
  num_frente?: string
  /** Único DENTRO da frente. */
  item: string
  /** Quatro valores, e cada um preenche um conjunto diferente de campos:
   *  `CARGA(S) ATENDIDA(S) - GCT` traz operador, máquina e medição;
   *  `SOLICITACAO DE CARGA(S)` traz só data, pedágio e notas;
   *  `CARGA(S) ATENDIDA(S) - TMS` traz motorista, veículo, viagem, CTe e MDFe;
   *  `AGENDAMENTO DE VIAGEM` traz motorista, veículo e viagem. */
  desc_ocorrencia: string | null
  data_agendamento: string | null
  codigo_motorista: string | null
  nome_motorista: string | null
  /** A PLACA, que é o `codigo` do getveiculos. Nem toda placa daqui existe lá. */
  codigo_veiculo: string | null
  codigo_reboque: string | null
  filial_viagem: string | null
  viagem: string | null
  status_viagem: string | null
  /** A viagem TEM pedágio? Booleano desde 08/09/2026: a origem sempre mandou SIM
   *  e NAO, e o campo era lido como valor em reais — a coluna ficou nula por doze
   *  dias sem ninguém ver. */
  pedagio: boolean | null
  /** Estado da INFORMAÇÃO do vale pedágio, cru como a origem manda. Vocabulário da
   *  legenda Informações Vale Pedágio do Painel Cockpit do TOTVS. */
  status_pedagio: string | null
  codigo_operador: string | null
  nome_operador: string | null
  /** `M1` ou `M5`, sem descrição. O de para é pendência aberta com a origem. */
  tipo_maquina: string | null
  codigo_maquina: string | null
  /** As quatro listas netas. SEMPRE arrays, nunca nulas: a origem manda as quatro
   *  chaves em toda ocorrência, e a que não tem item manda vazia. */
  medicao: FrenteMedicao[]
  notas: FrenteNota[]
  cte: FrenteCte[]
  mdfe: FrenteMdfe[]
}

/** Medição do trabalho de campo, a primeira interface do requisito 4.
 *  `quantidade` vem com PONTO decimal, ao contrário do peso do getSubcontratos. */
export interface FrenteMedicao {
  numero_medicao: string
  data_inicial: string
  data_final: string
  quantidade: string
}

export interface FrenteNota {
  lote_solicitacao: string
  chave_nfe: string
  status_solicitacao: string
  /** AAAAMMDD e HH:MM, parede local. Publicados em 08/09/2026. */
  data_solicitacao: string
  hora_solicitacao: string
}

export interface FrenteCte {
  lote_cte: string
  filial_cte: string
  numero_cte: string
  serie_cte: string
  chave_cte: string
  situacao_cte: string
}

export interface FrenteMdfe {
  numero_mdfe: string
  chave_mdfe: string
  situacao_envio: string
  situacao_encerra: string
}

/** Cabeçalho da grade de Frentes: último ciclo, status cru e os três de trilho.
 *  As contagens de trilho ocupam o lugar de `vigentes`, que não existe aqui: elas
 *  respondem quantas frentes o HUB consegue ligar a cada lado da safra. */
export interface FrenteResumo {
  synced_at: string | null
  total: number
  com_transporte: number
  com_campo: number
  com_os_dois: number
  /** Quantas já estão ligadas a uma frente de trabalho do HUB. */
  vinculadas: number
  por_status: { status: string | null; total: number }[]
}

/** Referência curta da planilha de campo, dentro da frente de trabalho.
 *  Simétrica de `SubcontratoRef`: aquela é o trilho de transporte, esta é o de
 *  campo. */
export interface PlanilhaRef {
  uuid: string
  contrato: string
  planilha: string
  subcontrato: string | null
  /** Fantasia quando existe, razão social quando não. A razão social vai ao lado. */
  cliente: string | null
  cliente_razao_social: string | null
  /** O PRODUTOR onde o serviço acontece. Não é o cliente. */
  cooperante: string | null
  cooperante_razao_social: string | null
  pivot: string | null
  safra: string | null
  ano: string | null
  /** Realizado quando existe, previsto quando não. `periodo_realizado` diz qual. */
  inicio: string | null
  fim: string | null
  periodo_realizado: boolean
  status: string | null
}

/** Referência curta do subcontrato, dentro da carga e da frente de trabalho. */
export interface SubcontratoRef {
  uuid: string
  subcontrato: string
  /** Contrato-mãe no Protheus. Vai junto porque a chave é o PAR: entre os
   *  vigentes há 1877 linhas para 786 números, e `V-26/0001.0000 TC` aparece 16
   *  vezes sob 16 contratos, cada uma um negócio diferente. */
  contrato: string | null
  descricao: string | null
  cliente: string | null
  rota: string | null
  lavoura: string | null
  dupla: string | null
  vigente: boolean
}

/** Carimbos do alerta de Observação de uma viagem (Complemento §2). */
export interface ShipmentObservation {
  created_at: string | null
  created_by: { uuid: string; name: string } | null
  resolved_at: string | null
  resolved_by: { uuid: string; name: string } | null
}

/** Evento de auditoria da viagem (Complemento §6) — histórico por viagem. Espelha
 *  DescargaStatusEvent, com `event_type` distinguindo status × observação. */
export interface ShipmentEvent {
  uuid: string
  event_type: 'trip_status' | 'observacao_aberta' | 'observacao_resolvida' | 'nao_atendido' | 'atendido' | 'reabertura' | 'reagendamento'
  from_status: string | null
  from_status_label: string | null
  to_status: string | null
  to_status_label: string | null
  note: string | null
  changed_at: string
  changed_by: { uuid: string; name: string } | null
}

/** Maquinário alocado a um contrato/frente (2ª tabela da Frente de Trabalho).
 *  Máquina + operador vêm do conjunto de maquinário. */
/** Uma máquina e o seu operador dentro de uma programação de colheita.
 *
 *  `maquinario_uuid` é a linha do Maquinário que foi marcada, e é por ela que a
 *  janela de edição remarca as caixas. Vem nulo quando a linha já saiu da frente:
 *  o participante continua na colheita, e não há mais caixa para marcar. */
export interface ContractColheitaParticipante {
  maquinario_uuid: string | null
  maquina: string | null
  operador: string | null
}

/** Uma programação de colheita da frente: dia, horário, cargas previstas e equipe.
 *
 *  É UM registro por programação, não um por máquina: quatro máquinas numa
 *  programação de 20 cargas são 20 cargas no total, e não 80. */
export interface ContractColheita {
  uuid: string
  contract_uuid: string | null
  /** Derivado da data e da hora: "Colheita 21/09/2026 07:00". Acompanha a edição. */
  nome: string
  /** YYYY-MM-DD. */
  data: string
  /** HH:MM. */
  hora: string
  /** Na tela: "cargas solicitadas". */
  cargas_programadas: number
  /** O lançamento do atendido, digitado na Frente. Na tela: "cargas atendidas".
   *  `null` é NÃO LANÇADO, e nunca zero atendido: zero é um lançamento. */
  cargas_colhidas: number | null
  /** Por que o atendido ficou abaixo do solicitado. Obrigatória só nesse caso, e
   *  vazia sem lançamento. Não confundir com `notes`, o recado livre da operação. */
  justificativa: string | null
  notes: string | null
  participantes: ContractColheitaParticipante[]
  created_at?: string | null
}

export interface ContractMaquinario {
  uuid: string
  contract_uuid: string | null
  /** Máquina (fleet) e operador (personnel) ISOLADOS — sem conjunto. */
  machine: FleetRef | null
  operator: PersonRef | null
  start_date: string | null
  end_date: string | null
  hectares: number | null
  /** Preenchido pelo cadastro da pessoa ao escolher o operador (04/09/2026). */
  operator_code: string | null
  /** "Mini Gráficos" — código/texto livre da planilha (ex.: 000069). */
  mini_grafico: string | null
  /** O hotel do operador, DERIVADO da ocupação em aberto dele no módulo de
   *  Hospedagem desde 09/09/2026. Somente leitura: a linha não escolhe hotel.
   *  `lodging` é o nome, `lodging_place` o local com o link fixo do cadastro e
   *  `lodging_checkin` a data do check-in. Nulo quer dizer operador sem
   *  hospedagem em aberto, que é pendência da operação. */
  lodging: string | null
  lodging_place?: (PlaceRef & { link?: string | null }) | null
  lodging_checkin?: string | null
  /** Preenchida pelo utilitário ligado ao operador em Conjuntos (04/09/2026). */
  light_fleet: string | null
  notes: string | null
  /** A ocorrência de campo do Protheus equiparada a esta linha, pelo de-para do
   *  maquinário (04/09/2026), e os números de medição dela. */
  ocorrencia?: MaquinarioOcorrenciaRef | null
  medicoes?: string[]
  ocorrencias_recusadas?: string[]
  /** A primeira e a última programação da Colheita em que a máquina entrou nesta
   *  frente (19/10/2026). Nulas quando ela ainda não foi programada. */
  programacao_inicio?: string | null
  programacao_fim?: string | null
  created_at?: string | null
}

/** Resposta da transferência: a linha da origem, que não muda, a do destino e a viagem. */
export interface MaquinarioTransferido {
  origem: ContractMaquinario
  destino: ContractMaquinario
  transporte: { uuid: string; registro_hub: string | null }
}

/** Recorte da ocorrência de CAMPO embutido na linha do maquinário. */
export interface MaquinarioOcorrenciaRef {
  uuid: string
  num_frente: string | null
  item: string
  data_agendamento: string | null
  codigo_operador: string | null
  nome_operador: string | null
  tipo_maquina: string | null
  codigo_maquina: string | null
  medicao: FrenteMedicao[]
}

/** Uma solicitação de abastecimento (04/09/2026): os textos congelados na hora
 *  de salvar, mais quem gerou e quando, para a imagem ser redesenhada igual. */
/** Uma mensagem do teclado do rastreador (Mensagens Teclado, 10/09/2026).
 *  É a macro CRUA de `tracker_position`, já interpretada pelo servidor: `tipo` diz
 *  qual das duas macros é e `informacao` é o que o motorista digitou. */
export interface MensagemTeclado {
  /** `tracker_position` não tem uuid: a linha de histórico é imutável. */
  id: number
  enviada_em: string | null
  recebida_em: string | null
  placa: string | null
  veiculo_uuid: string | null
  veiculo_categoria: Categoria | null
  motorista: string | null
  motorista_uuid: string | null
  /** motID da TrucksControl. Vem preenchido mesmo sem de para com o cadastro. */
  motorista_tracker_id: number | null
  /** Dupla HUB de HOJE do cavalo da mensagem (24/09/2026). Nula sem dupla. */
  dupla: string | null
  dupla_uuid: string | null
  tipo: 'nota_fiscal' | 'veiculo_vazio' | null
  /** O que aparece na coluna Informação enviada: nº da nota ou data e hora. */
  informacao: string | null
  nota_numero: string | null
  /** Instante local de parede em que o veículo ficou vazio, sem fuso. */
  vazio_em: string | null
  macro: string | null
  lat: number | null
  lon: number | null
  maps_url: string | null
  municipio: string | null
  uf: string | null
  rodovia: string | null
  rua: string | null
  /** Conferência manual (24/09/2026). Mensagem nunca mexida vem `pendente`,
   *  com autor e hora nulos. */
  status: MensagemTecladoStatus
  status_alterado_por: string | null
  status_alterado_em: string | null
}

export type MensagemTecladoStatus = 'pendente' | 'conferido'

/** A conferência das macros por viagem da Frente (Samuel, 19/10/2026): toda viagem
 *  fechada deve uma macro de nota e uma de veículo vazio. A lista só traz o que
 *  faltou, uma linha por viagem e macro. */
export type FaltaDeMacro = 'nota_fiscal' | 'veiculo_vazio'

export interface FaltaDeMacroLinha {
  id: string
  shipment_uuid: string
  numero: string
  dia: string
  contrato: string | null
  contract_uuid: string | null
  placa: string | null
  veiculo_categoria: MensagemTeclado['veiculo_categoria']
  motorista: string | null
  motorista_uuid: string | null
  dupla: string | null
  dupla_uuid: string | null
  falta: FaltaDeMacro
  /** A janela em que a macro foi procurada: do início do ciclo da viagem ao início
   *  da viagem seguinte do cavalo, ou ao fim do ciclo com a folga. */
  janela_inicio: string
  janela_fim: string
}

export interface FaltaDeMacroRanking {
  motorista: string | null
  motorista_uuid: string | null
  placas: string[]
  viagens: number
  sem_nota: number
  sem_vazio: number
  total: number
}

export interface ConferenciaDeMacros {
  itens: FaltaDeMacroLinha[]
  ranking: FaltaDeMacroRanking[]
  indicadores: { viagens: number; sem_nota: number; sem_vazio: number; em_dia: number; em_dia_pct: number | null }
  /** O piso da conferência, a primeira data cobrada. */
  inicio: string
  janela: { de: string; ate: string }
  duplas: { uuid: string; name: string }[]
}

/** Um abastecimento lançado no Protheus, do espelho do `getabas` (Samuel, 16/10/2026).
 *  Só leitura: quem lança é o ERP, e a aba Abastecimentos mostra o que ele registrou.
 *  O hodômetro vem cru, como o ERP escreve. */
export interface AbastecimentoProtheus {
  uuid: string
  num_abastecimento: string
  data: string | null
  hora: string | null
  placa: string | null
  modalidade: string | null
  /** O código da categoria da frota pela placa: `espiga`, `prancha`, `utilitario`. */
  categoria: string | null
  tipo_abastecimento: 'interno' | 'externo'
  km_rodado: number | null
  km_l: number | null
  /** Do cadastro do colaborador no HUB (19/10/2026). Sem cadastro, a tela escreve
   *  FORA DO HUB nas duas colunas. A função é a vigente na data do abastecimento. */
  colaborador_no_hub: boolean
  base: string | null
  funcao: string | null
  /** A dupla da coluna Dupla do Painel de Diárias no dia do abastecimento: a do
   *  lançamento do ERP e, sem ele, a do cadastro (21/10/2026). */
  dupla: string | null
  /** Só na Espiga: o KM vazio e o cheio entre o abastecimento anterior e este. Nulo
   *  na Espiga é Sem dados. */
  km_vazio_pct: number | null
  km_cheio_pct: number | null
  posto: { codigo: string | null; loja: string | null; cnpj: string | null; nome: string | null; cidade: string | null; uf: string | null }
  combustivel: { codigo: string | null; nome: string | null }
  tanque: string | null
  bomba: string | null
  quantidade: number | null
  valor_unitario: number | null
  valor_total: number | null
  hodometro: string | null
  motorista: { codigo: string | null; nome: string | null; filial: string | null; matricula: string | null }
}

/** A página do espelho, com o resumo do filtro INTEIRO e não só da página. */
export interface AbastecimentosProtheusPagina extends Paginated<AbastecimentoProtheus> {
  resumo: {
    litros: number
    valor_total: number
    abastecimentos: number
    km_rodado: number
    media_geral: number | null
    por_combustivel: Record<'S10' | 'S500' | 'Arla' | 'Etanol' | 'Gasolina', { litros: number; preco_medio: number | null }>
    internos: number
    externos: number
    internos_pct: number
    externos_pct: number
  }
}

/** Uma linha de ranking da aba Indicadores dos abastecimentos (19/10/2026). Nas de
 *  média, a média é consolidada: total de km sobre total de litros. */
export interface AbastecimentoRankingMedia {
  chave: string
  nome: string | null
  fora_do_hub: boolean
  km: number
  litros: number
  abastecimentos: number
  media: number | null
}

export interface AbastecimentoRankingLitros {
  chave: string
  nome: string | null
  litros: number
  abastecimentos: number
}

export interface AbastecimentoIndicadoresResposta {
  rankings: {
    motoristas: AbastecimentoRankingMedia[]
    prancheiros: AbastecimentoRankingMedia[]
    bases_espiga: AbastecimentoRankingMedia[]
    bases_prancha: AbastecimentoRankingMedia[]
    operadores: AbastecimentoRankingMedia[]
    postos_internos: AbastecimentoRankingLitros[]
    postos_externos: AbastecimentoRankingLitros[]
  }
  resumo: AbastecimentosProtheusPagina['resumo']
}

export type AbastecimentoAlertaStatus = 'pendente' | 'corrigido' | 'justificado'
export type AbastecimentoAlertaTipo = 'media_abaixo' | 'media_acima' | 'externo_maquina' | 'externo_filial'
export type AbastecimentoAlertaRegra = 'media' | 'posto_externo'

/** Um alerta do Relatório de Alertas dos abastecimentos, um por abastecimento e
 *  regra: a média fora da faixa da meta e, desde 21/10/2026, o posto externo. O
 *  retrato é a última vez em que o abastecimento estava na regra; o abastecimento é
 *  o de agora, nulo quando ele saiu do Protheus. */
export interface AbastecimentoAlertaLinha {
  uuid: string
  num_abastecimento: string
  dia: string | null
  placa: string | null
  /** O código da categoria da frota; nulo para a placa sem cadastro. */
  categoria: string | null
  regra: AbastecimentoAlertaRegra
  tipo: AbastecimentoAlertaTipo
  status: AbastecimentoAlertaStatus
  retrato: {
    km?: number | null
    litros: number | null
    media?: number | null
    meta?: number | null
    minimo?: number | null
    maximo?: number | null
    hodometro?: number | null
    hodometro_anterior?: number | null
    posto?: string | null
    cidade?: string | null
    uf?: string | null
    combustivel?: string | null
  } | null
  /** Só na média. */
  faixa: { meta: number; minimo: number; maximo: number } | null
  media_atual: number | null
  detectado_em: string | null
  corrigido_em: string | null
  corrigido_motivo: 'media' | 'cadastro' | 'removido' | null
  justificativa: string | null
  justificado_por: string | null
  justificado_em: string | null
  abastecimento: AbastecimentoProtheus | null
}

export interface AbastecimentoAlertas {
  itens: AbastecimentoAlertaLinha[]
  total: number
  page: number
  pages: number
  per_page: number
  indicadores: Record<AbastecimentoAlertaStatus, number>
}

export interface SolicitacaoAbastecimento {
  uuid: string
  contract_uuid: string | null
  contract: string | null
  maquinario_uuid: string | null
  operator: PersonRef | null
  machine: FleetRef | null
  driver: PersonRef | null
  responsavel: { uuid: string; name: string } | null
  data: string
  operador_nome: string | null
  cpf: string | null
  maquina: string | null
  cooperante: string | null
  fazenda: string | null
  hora_maquina: number | null
  litros: number
  motorista_nome: string | null
  placa_utilitario: string | null
  observacao: string | null
  created_at: string | null
}

/** Ponta (origem/destino) DERIVADA de uma viagem: produtor/fazenda/cidade do
 *  contrato — ou nome/cidade do depósito (place). Read-only na UI. */
export interface TransporteEndpoint {
  /** De que ponta se trata. `garagem` é caso de verdade, não ponta incompleta: a
   *  máquina sai da garagem e volta para ela o tempo todo, e garagem não tem contrato
   *  nem cooperante. Quem lê a ponta usa isto para trocar de rótulo em vez de mostrar
   *  um vazio que se lê como pendência (Samuel, 09/09/2026). */
  tipo?: 'contrato' | 'garagem' | 'local' | null
  /** O Local da ponta (15/10/2026): o Local escolhido na grade, ou a fazenda do
   *  contrato na viagem antiga que nunca teve Local. */
  local?: string | null
  /** Número do contrato do HUB. Voltou a ser isto em 09/09/2026: por um dia a coluna
   *  mostrou a LAVOURA do subcontrato, e o Samuel desfez, porque é o cadastro de
   *  Local que tem coordenada e é por ela que a viagem é monitorada. A lavoura virou
   *  campo próprio, `destination_subcontrato`, escolhido à mão e só no destino.
   *  Null quando a ponta é garagem: aí a célula mostra o nome dela. */
  contrato: string | null
  sender: string | null
  farm: string | null
  city: string | null
  uf: string | null
}

/** Lavoura de destino da viagem, escolhida na grade entre os subcontratos do ERP.
 *  Nono grupo da linha (Samuel, 09/09/2026). Só o destino tem. */
export interface TransporteLavouraDestino {
  uuid: string
  lavoura: string | null
  /** O par que é a chave do subcontrato. Desempata as lavouras repetidas: são 2416
   *  códigos distintos em 2943 linhas, e mil linhas dividem o código com outra. */
  subcontrato: string
  contrato: string | null
  /** Pendentes no ERP em 09/09/2026, os dois pedidos ao Mateus. Vêm nulos até lá, e
   *  o servidor já os lê por getattr para aparecerem sozinhos quando existirem. */
  inscricao_estadual: string | null
  cpf_cnpj: string | null
}

/** Frete: categoria (não valor). */
export type FreightType = 'casa' | 'a_cobrar'

/** Viagem de transporte de máquina (aba "Transporte maquina" da planilha).
 *  Move uma colheitadeira/trator de uma origem (contrato/depósito) para um destino. */
export interface TransporteMaquina {
  uuid: string
  customer: { uuid: string; name: string; empresa_pai?: string | null } | null
  /** Dupla de backoffice responsável (atribuição/filtro — não escopo de leitura). */
  dupla: { uuid: string; name: string } | null
  /** Início e Fim da prancha nascem da virada de status desde 22/10/2026: o Início na
   *  entrada em Trânsito e o Fim no Finalizado. Só a máquina que foi rodando os digita. */
  start_date: string | null
  end_date: string | null
  /** A Data prevista, a única data digitada da prancha (22/10/2026). */
  data_prevista?: string | null
  /** Status da VIAGEM (`transport_status`): andamento/finalizado/apontado. */
  status: string | null
  /** Status do VEÍCULO (`transport_vehicle_status`), avançado por geocerca:
   *  escalado → embarcando → transito → disponivel. Somente leitura na tela. */
  vehicle_status?: string | null
  /** O STATUS ÚNICO da movimentação (15/10/2026), derivado dos dois eixos acima. É o
   *  único que a grade mostra e escreve, pelo campo `situacao` do PATCH. */
  situacao?: TransporteSituacao | null
  /** A viagem prevista (Samuel, 28/10/2026): a predecessora, que precisa finalizar
   *  antes, quem a escolheu e quando, e por que a prevista pode estar sem quem a
   *  libere. `dependente` é o outro lado, a viagem que espera por esta. */
  predecessora?: TransportePredecessora | null
  predecessora_bloqueio?: PredecessoraBloqueio | null
  predecessora_definida_em?: string | null
  predecessora_definida_por?: { uuid: string; name: string } | null
  dependente?: (TransporteRef & { situacao: TransporteSituacao | null }) | null
  driver: PersonRef | null
  truck: FleetRef | null
  trailer: FleetRef | null
  truck_plate: string | null
  trailer_plate: string | null
  /** A máquina foi RODANDO até o destino, sem prancha (Samuel, 24/09/2026). Registro
   *  e cor: marcada, a linha fica roxa, e nada mais na viagem muda. */
  sem_prancha?: boolean
  origin_contract: ContractRef | null
  destination_contract: ContractRef | null
  origin_place: PlaceRef | null
  destination_place: PlaceRef | null
  /** Lavoura de destino, escolhida à mão. Fora de `destination` de propósito: aquele
   *  é derivado do contrato, este é uma escolha guardada em coluna. */
  destination_subcontrato?: TransporteLavouraDestino | null
  /** Remetente/Fazenda/Cidade derivados da ponta (contrato ou depósito). */
  origin: TransporteEndpoint | null
  destination: TransporteEndpoint | null
  /** Máquina transportada. Categoria originada da Modalidade Máquina. */
  machine: FleetRef | null
  /** A segunda máquina da mesma prancha (15/10/2026), vazia quando ela leva uma só. */
  machine_2?: FleetRef | null
  /** Do Protheus desde 15/10/2026: vazio e bloqueado antes do vínculo. */
  km: number | null
  trip_number: string | null
  freight_type: FreightType | null
  /** A viagem do Protheus vinculada (Samuel, 28/09/2026): o status ao vivo do
   *  espelho, e o frete casa e o km_total de onde os dois campos acima vieram. */
  protheus_viagem?: ViagemProtheusDoTransporte | null
  /** As datas da viagem vinculada no ERP e o que diverge das do HUB (15/10/2026). */
  datas_protheus?: TransporteDatasProtheus | null
  /** As placas que diferem das da viagem vinculada no Protheus (26/10/2026). Lista
   *  vazia é a conferida sem diferença; nulo, a viagem sem vínculo. */
  placas_protheus?: { campo: 'cavalo' | 'carreta'; hub: string | null; erp: string | null }[] | null
  /** A Observação da movimentação, texto livre do grupo Contrato Destino. Não é o
   *  alerta de observação, que mora em `notes`. */
  observacao?: string | null
  /** Texto da observação (preenchido quando o alerta está ativo). */
  notes?: string | null
  /** Alerta de observação ativo → linha azul até resolver (igual à Frente). */
  has_observation?: boolean
  observation?: ShipmentObservation | null
  /** Identificação da viagem no HUB, TM-000123. Nasce na criação e nunca muda: é a
   *  chave pela qual o faturamento acha a viagem pelo assunto do e-mail. */
  registro_hub?: string | null
  /** Quem adicionou a viagem. */
  created_by?: { uuid: string; name: string } | null
  /** QUAL nota o faturamento emite. Nasce em branco, é escolhida na grade e TRAVA o
   *  envio do e-mail: no começo da safra as máquinas sobem para Mato Grosso com nota de
   *  transferência entre filiais, e é só de lá que saem as de remessa. */
  nota_tipo?: NotaTipo | null
  /** A viagem de retorno real (21/10/2026): a remessa de onde este retorno nasceu, e
   *  o retorno que esta remessa já gerou. */
  retorno_de?: TransporteRef | null
  retorno_gerado?: TransporteRef | null
  /** A conferência com o Painel de Diárias (21/10/2026): cada dia do período sem
   *  Trabalho, com o lançamento achado, nulo quando não há. Vazio é a viagem sem
   *  divergência; nulo, a que não se confere. */
  diarias_divergentes?: { dia: string; status: string | null }[] | null
  /** Eixo do DOCUMENTO, que não é o da viagem nem o do veículo. A remessa é escrita
   *  pelo envio do e-mail e não tem editor na grade; o retorno é manual. */
  remessa_status?: RemessaStatus | null
  remessa_sent_at?: string | null
  remessa_sent_by?: { uuid: string; name: string } | null
  /** Nulo quando `nota_tipo` é `transferencia`: transferência entre filiais não tem
   *  nota de retorno, e o campo fica em branco e sem editor. */
  retorno_status?: RetornoStatus | null
  retorno_changed_at?: string | null
  retorno_changed_by?: { uuid: string; name: string } | null
  /** O pedido de encerramento do MDF-e da prancha, que sai sozinho na chegada a
   *  Finalizado com a nota enviada: pendente, enviado ou falha. */
  mdfe_encerramento_status?: 'pendente' | 'enviado' | 'falha' | null
  mdfe_encerramento_sent_at?: string | null
  created_at?: string | null
}

/** `previsto` desde 28/10/2026: a viagem planejada que espera a predecessora. Não se
 *  escolhe na grade; nasce da escolha da predecessora e sai pela liberação. */
export type TransporteSituacao = 'previsto' | 'escalado' | 'embarcando' | 'transito' | 'finalizado' | 'apontado'

/** Por que a viagem prevista está sem quem a libere: sem predecessora, com ela
 *  excluída, cancelada, que é a que voltou ao branco, ou já encerrada. */
export type PredecessoraBloqueio = 'sem_predecessora' | 'excluida' | 'cancelada' | 'encerrada'
export interface TransportePredecessora {
  uuid: string
  registro_hub: string | null
  situacao: TransporteSituacao | null
  excluida: boolean
}
/** Uma predecessora possível na janela da viagem prevista. A prevista que entra na
 *  lista traz a dela, e é assim que a corrente aparece na escolha. */
export interface PredecessoraCandidata {
  uuid: string
  registro_hub: string | null
  situacao: TransporteSituacao | null
  data: string | null
  origem: string | null
  destino: string | null
  maquina: string | null
  trip_number: string | null
  predecessora: TransporteRef | null
}
/** `GET /transporte-maquina/:uuid/predecessoras`. */
export interface PredecessorasDaViagem {
  motorista: { uuid: string; name: string } | null
  mensagem: string | null
  ocupantes: number
  items: PredecessoraCandidata[]
}

export interface TransporteDatasProtheus {
  inicio: string | null
  fim: string | null
  inicio_diverge: boolean
  fim_diverge: boolean
  diverge: boolean
}

export type RemessaStatus = 'nao_enviada' | 'enviada'
export type RetornoStatus = 'nao_solicitado' | 'solicitado'
/** `retorno` só na viagem criada pelo botão Gerar viagem de retorno (21/10/2026). */
export type NotaTipo = 'remessa' | 'transferencia' | 'retorno'
export interface TransporteRef { uuid: string; registro_hub: string | null }
/** A resposta do Gerar viagem de retorno: a viagem criada e o e-mail ao Fiscal. */
export interface RetornoGerado {
  viagem: TransporteMaquina
  email: { enviado: boolean; motivos?: string[]; destinatarios?: string[]; assunto?: string }
}

/** A viagem do Protheus como o Transporte de Máquina a vê (Samuel, 28/09/2026): o
 *  número, o status ao vivo do espelho e o que entra sozinho ao vincular. `sugestao`
 *  só vem na lista do seletor: é a viagem da placa do transporte ainda sem vínculo. */
export interface ViagemProtheusDoTransporte {
  uuid: string
  filial: string
  viagem: string
  /** "0105-084655", filial e viagem, como a operação escreve o Nº Viagem. */
  numero: string
  /** EM ABERTO, CHEGADA EM FILIAL, ENCERRADA ou CANCELADA, como o ERP escreve. */
  status_viagem: string | null
  data_prev_inicio: string | null
  /** A saída e a chegada apontadas no ERP desde 21/10/2026; null antes de a viagem
   *  sair e chegar. São elas que a Data Ini e a Data Fim conferem. */
  data_saida?: string | null
  data_chegada?: string | null
  /** Os botões CT-e e MDF-e da viagem vinculada. */
  documentos?: DocumentosFiscais | null
  placa_veiculo: string | null
  placa_reboque: string | null
  /** A segunda carreta do rodotrem, publicada pelo ERP desde 13/10/2026. */
  placa_2reboque: string | null
  nome_motorista: string | null
  destino: string | null
  fantasia_cliente: string | null
  contrato: string | null
  lavoura: string | null
  tipo_servico: string | null
  /** SIM vira Frete Casa e NAO vira Frete a Cobrar; null quando o ERP não mandou. */
  frete_casa: boolean | null
  /** `km_total` da viagem no ERP. Vazio em todas as viagens em 28/09/2026. */
  km: number | null
  sugestao?: boolean
}

/** O que a prévia da solicitação devolve: o que o e-mail vai dizer e o que ainda
 *  falta para ele poder sair. A tela mostra a pendência ANTES do clique. */
/** Uma ponta como o E-MAIL a descreve. Vocabulário do documento, em pt-BR: é o que o
 *  faturamento lê, e não os nomes do JSON da viagem. */
export interface RemessaPonta {
  /** `garagem` troca o bloco inteiro de rótulo: sem contrato e sem cooperante, o nome
   *  do local responde sozinho pela ponta. */
  tipo: 'contrato' | 'garagem' | 'local' | null
  local?: string | null
  contrato: string | null
  cooperante: string | null
  fazenda: string | null
  cidade: string | null
  uf: string | null
}

export interface RemessaMaquina {
  ordem: number
  identificacao: string | null
  modelo: string | null
  chassi: string | null
  plataforma: { vinculada?: boolean; modelo: string | null; chassi: string | null }
  viagem_anterior: { registro_hub: string | null; uuid: string } | null
}

export interface RemessaPreview {
  dados: {
    registro_hub: string | null
    /** O documento que o faturamento vai emitir. `documento` é o que vai no assunto,
     *  em caixa alta; `rotulo` é o que a tela mostra. */
    nota: { tipo: NotaTipo | null; rotulo: string | null; documento: string | null }
    /** Quem recebe em cópia: os dois fixos, mais o fiscal na transferência. */
    destinatarios: string[]
    transporte: { motorista: string | null; cavalo: string | null; carreta: string | null }
    maquina: { identificacao: string | null; modelo: string | null; chassi: string | null }
    plataforma: { modelo: string | null; chassi: string | null }
    /** As máquinas da prancha, uma ou duas, cada uma com a plataforma e, na remessa,
     *  a viagem anterior que recebe a Nota de Retorno (15/10/2026). */
    maquinas?: RemessaMaquina[]
    /** A Nota de Retorno fictício da viagem anterior, só na remessa. */
    retorno?: { pedir: boolean; itens: { maquina: string | null; registro_hub: string | null }[] }
    origem: RemessaPonta
    destino: RemessaPonta
    /** O grupo Contrato Destino, a antiga Lavoura. */
    contrato_destino?: { lavoura: string | null; subcontrato?: string | null; inscricao_estadual: string | null; cpf_cnpj: string | null }
    /** O nome antigo do mesmo bloco. */
    destino_lavoura: { lavoura: string | null; inscricao_estadual: string | null; cpf_cnpj: string | null }
    /** Se o pedido leva o grupo Contrato Destino: não na garagem nem na transferência
     *  entre filiais (16/10/2026). Ausente na prévia de antes do deploy. */
    exibir_contrato_destino?: boolean
    /** No retorno real (21/10/2026), a remessa de onde ele nasceu. */
    remessa_vinculada?: TransporteRef | null
  }
  bloqueios: string[]
  ja_enviada: boolean
}

// ── Fleet board (Lista de Descarga Única) ────────────────────────────

export type FleetBoardStatus = 'unloading' | 'transit' | 'loading' | 'available' | 'on_leave'

export interface FleetBoardShipmentRef {
  uuid: string
  number: string
  status: ShipmentStatus
  contract: string | null
  destination: string | null
  unload_date: string | null
  invoice: string | null
  notes: string | null
}

export interface FleetBoardItem {
  status_frota: FleetBoardStatus
  truck: FleetRef
  truck_plate: string | null
  trailer_plate: string | null
  driver: string | null
  carrier: string | null
  shipment: FleetBoardShipmentRef | null
}

/** GET /fleet-board — board único da frota. `contadores` traz cada status + `total`. */
export interface FleetBoard {
  contadores: Record<string, number>
  itens: FleetBoardItem[]
}

// ── Conjuntos / Duplas ───────────────────────────────────────────────

export interface DuplaMembro {
  uuid: string
  role_in_dupla: string | null
  user: { uuid: string; name: string; email: string } | null
}

/** Dupla de backoffice (delimita escopo operacional). */
export interface Dupla {
  uuid: string
  name: string
  code: string | null
  active: boolean
  notes: string | null
  created_at: string | null
  membros?: DuplaMembro[]
  /** Escopo-mãe: clientes cujos contratos/frotas/pessoal entram por customer_id. */
  customers?: { uuid: string; name: string }[]
  /** Frotas avulsas (além das derivadas por cliente). */
  frotas?: FleetRef[]
  /** Pessoal operacional avulso (além do derivado por cliente). */
  pessoal?: PersonRef[]
}

/** Conjunto de transporte: motorista + cavalo + carreta(s). */
export interface ConjuntoCaminhao {
  uuid: string
  name: string | null
  customer_uuid: string | null
  driver: PersonRef | null
  truck: FleetRef | null
  trailer1: FleetRef | null
  trailer2: FleetRef | null
  active: boolean
  notes: string | null
  created_at: string | null
}

/** Conjunto de maquinário: operador + máquina. */
export interface ConjuntoMaquinario {
  uuid: string
  name: string | null
  customer_uuid: string | null
  operator: PersonRef | null
  machine: FleetRef | null
  /** A plataforma (04/09/2026): o único tipo de veículo cadastrado à mão. */
  platform?: FleetRef | null
  active: boolean
  notes: string | null
  created_at: string | null
}

/** Conjunto de utilitário: operador mais veículo utilitário, a terceira aba de
 *  Conjuntos (04/09/2026). É de onde a Frota Leve do maquinário sai. */
export interface ConjuntoUtilitario {
  uuid: string
  name: string | null
  customer_uuid: string | null
  operator: PersonRef | null
  vehicle: FleetRef | null
  active: boolean
  notes: string | null
  created_at: string | null
}

export type TransferStatus = 'pending' | 'accepted' | 'rejected' | 'cancelled'

/** Transferência de conjunto entre duplas. */
export interface Transferencia {
  uuid: string
  conjunto: ConjuntoCaminhao | null
  from_dupla: { uuid: string; name: string } | null
  to_dupla: { uuid: string; name: string } | null
  requested_by: PersonRef | null
  responsible_user: PersonRef | null
  approver: PersonRef | null
  status: TransferStatus
  status_label?: string
  requested_at: string | null
  resolved_at: string | null
  transferred_at: string | null
  notes: string | null
  created_at: string | null
}

// ── Painel de Descarga ───────────────────────────────────────────────

export type DescargaStatus =
  | 'advancing' | 'available' | 'scheduled' | 'loading' | 'transit' | 'unloading'
  | 'maintenance' | 'day_off' | 'other_prog' | 'operators' | 'away'

/** Estado da jornada do motorista na ATS, casado por CPF no servidor (Fase 02a).
 *  Tempos em SEGUNDOS; datas como texto da ATS. null na linha = sem match ou
 *  poller desligado. Ver docs/ats-jornada/07-leitura-jornada.md. */
export interface PainelJornada {
  status_dia: string | null
  status_ativo: string | null
  tempo_no_status_seg: number | null
  tempo_jornada_seg: number | null
  tempo_refeicao_seg: number | null
  tempo_repouso_seg: number | null
  tempo_hora_extra_seg: number | null
  inicio_jornada: string | null
  previsao_disponibilidade: string | null
  dias_sem_dsr: number | null
  placa: string | null
  atualizado_em: string | null
  /** Blocos de marcação do dia (mini linha do tempo). Presente quando o cache de
   *  marcações está aquecido; ver JornadaSegmento. */
  segmentos?: JornadaSegmento[] | null
  /** Fim de jornada (texto cru ATS); vazio = jornada aberta. Opcional: cache
   *  anterior ao deploy não tem o campo. */
  fim_jornada?: string | null
  /** Última localização textual da ATS, com acentos já consertados no servidor. */
  ultimo_local?: string | null
  ultimo_local_em?: string | null
  /** Jornada líquida (regra do cliente: total menos refeição e repouso), em
   *  segundos. Ausente/null → mostrar tempo_jornada_seg como fallback. */
  jornada_liquida_seg?: number | null
}

/** Faixa de alerta da jornada líquida: 8h de jornada, extensível até 12h. */
export type JornadaAlerta = 'nenhum' | 'ate4' | 'acima4'

/** Batida de jornada (marcação ATS crua) para o dialog de histórico do dia.
 *  Horários como texto da ATS ("AAAA-MM-DD HH:MM:SS"); `instantanea` = marco de
 *  início/fim (sem extensão); `fim` null = em aberto. */
export interface JornadaBatida {
  tipo: string
  he: boolean
  instantanea: boolean
  label: string
  inicio: string
  fim: string | null
  duracao_seg: number | null
  origem: string | null
  localizacao: string | null
  /** A posição que a ATS carimbou na batida, pela hierarquia dela: rastreador,
   *  aplicativo e, na falta, onde o veículo estava até 4 horas antes ou depois.
   *  Nulos quando a origem não posicionou. Vira o atalho do mapa no diálogo. */
  lat?: number | null
  lng?: number | null
}

/** GET /painel-descarga/:uuid/jornada-batidas — batidas do dia do motorista. */
export interface JornadaBatidas {
  nome: string | null
  truck_plate: string | null
  dia: string
  agora_min: number
  atualizado_em: string | null
  eventos: JornadaBatida[]
}

/** Agregação do Painel de Jornada (GET /dashboard/backoffice/jornada). Retrato do
 *  agora sobre toda a frota de motoristas, escopado. `por_status` traz o texto cru
 *  do StatusAtivo; a UI colore e agrupa. */
export interface JornadaDashboard {
  atualizado_em: string | null
  total_motoristas: number
  em_jornada: number
  dirigindo: number
  descansando: number
  por_status: Record<string, number>
  dsr_risco: { nome: string; dias_sem_dsr: number; status_ativo: string | null }[]
  ficando_disponivel: { nome: string; previsao: string; status_ativo: string | null }[]
}

/** Bloco da linha do tempo de jornada (GET /dashboard/backoffice/jornada/analytics).
 *  Minutos desde a meia-noite LOCAL do `dia`; `fim_min` null = em aberto (desenhar
 *  até `agora_min`). `he` marca hora extra; `tipo` é canônico (direcao, parado,
 *  refeicao, repouso, descanso, espera, outro) e `label` o texto cru da ATS. */
export interface JornadaSegmento {
  tipo: string
  he: boolean
  label: string
  ini_min: number
  fim_min: number | null
}

export interface JornadaMotoristaDia {
  nome: string
  filial: string | null
  /** A base DESCRIÇÃO do cadastro, inteira: "TRANSGRAOS - FORMOSA". É o que a
   *  coluna e o filtro Base da Análise do dia mostram, sem o nome da empresa
   *  (Samuel, 28/09/2026). A filial é a mesma coisa em código. */
  base?: string | null
  /** Rótulo da categoria do cadastro: "Motorista" ou "Operador". */
  funcao: string | null
  /** Código da categoria — o filtro casa por aqui, não pelo rótulo traduzido. */
  funcao_code: string | null
  /** Personnel.uuid — chave do dialog de batidas por motorista (com ou sem conjunto). */
  motorista_uuid: string | null
  placa: string | null
  /** A placa RESOLVIDA da jornada, pela macro do rastreador. É o que a coluna
   *  Veículo mostra: placa da macro, "SEM VEÍCULO" na jornada sem macro dentro
   *  da vigência, null em dia sem jornada ou anterior a 28/08/2026. */
  veiculo: string | null
  status_ativo: string | null
  /** Minuto do dia em que a situação atual começou. A tela conta a partir daqui e
   *  segue contando entre dois ciclos do poller. null = sem dado confiável. */
  situacao_desde_min: number | null
  dias_sem_dsr: number | null
  inicio_jornada: string | null
  fim_jornada: string | null
  ultimo_local: string | null
  /** Onde a pessoa estava quando bateu cada MARCO, com a coordenada da própria
   *  batida. O texto alimenta o Histórico; na Análise do dia entram só as
   *  coordenadas, que viram o atalho para o mapa ao lado da hora. */
  local_inicio?: string | null
  local_inicio_lat?: number | null
  local_inicio_lng?: number | null
  local_fim?: string | null
  local_fim_lat?: number | null
  local_fim_lng?: number | null
  /** Snapshot presente e sem fim de jornada. */
  jornada_aberta: boolean
  /** Existe jornada aberta HOJE (Samuel, 21/08/2026). Só ela mede: totais,
   *  bordas, descarga, status, infrações e rankings leem só a jornada cujo Início
   *  caiu hoje. A linha sem esta bandeira continua na lista, com o Início de
   *  ontem e Status Aberta, porque é assim que a operação acha quem esqueceu de
   *  fechar. Os KPIs de jornada e os rankings a deixam de fora. */
  jornada_hoje: boolean
  /** Jornada líquida (total menos refeição e repouso) em minutos; null sem dado.
   *  É o que a tela chama de Horas trabalhadas. */
  jornada_liquida_min: number | null
  /** A hora extra que a ATS MARCOU, desde 22/09/2026: o HUB não calcula quanto
   *  foi extra, replica. O total e cada uma das duas marcações que o produzem, 122
   *  Direção - Hora Extra e 156 Parado - Hora Extra; direção mais parado é o total. */
  hora_extra_min: number
  hora_extra_direcao_min: number
  hora_extra_parado_min: number
  /** O MESMO total classificado pelo dia da abertura: domingo e feriado a 100%,
   *  os demais dias a 50%. É a única parte da regra que ficou no HUB, porque a ATS
   *  não informa percentual. 50% mais 100% é sempre `hora_extra_min`. */
  he50_min: number
  he100_min: number
  alerta: JornadaAlerta
  /** Jornada aberta há 6 horas ou mais sem refeição registrada (23/09/2026). Só no
   *  dia corrente: fechada, ela vira a infração de refeição. */
  ausencia_refeicao?: boolean
  /** Vínculo com o Painel de Descarga (conjunto ativo do motorista), se houver. */
  descarga_status: string | null
  /** `transporte` quando o status é o do VEÍCULO na viagem de prancha, escalado,
   *  embarcando ou trânsito, no lugar do conjunto de grãos (13/10/2026). Nulo é o
   *  Painel de Descarga. */
  descarga_status_kind?: 'transporte' | null
  conjunto_uuid: string | null
  truck_plate: string | null
  /** Cliente da operação. O servidor só preenche nas quatro etapas de viagem —
   *  fora delas não há operação em curso e a coluna mostra travessão. */
  cliente: string | null
  /** Dupla dona do conjunto (Painel de Descarga). null sem vínculo. */
  dupla: string | null
  segmentos: JornadaSegmento[]
  /** Minutos do dia por tipo, mais `hora_extra` acumulada em paralelo. Só isso:
   *  métrica nova entra em chave própria, nunca aqui dentro. */
  totais: Record<string, number>
  /** Parado colado na abertura e no encerramento da jornada. null sem marco. */
  parado_bordas: JornadaBordas | null
  /** Jornada que caiu dentro do status Descarregando. null sem viagem. */
  descarga_jornada: JornadaNaDescarga | null
  /** Jornada distribuída pelas etapas do Painel de Descarga. null sem conjunto —
   *  não confundir com zero, que é "medido e deu zero". */
  tempo_por_status_operacional: JornadaPorStatus | null
  /** Ocorrências de infração das jornadas abertas hoje, uma por item: duas
   *  corridas de direção acima de 5h30 são dois itens. Vazia sem jornada hoje. */
  infracoes: JornadaInfracao[]
  /** Tempos das viagens do motorista no dia. null sem viagem. */
  viagens: ViagemTempos[] | null
  /** Distância GPS da jornada em METROS. null = sem conjunto ou sem cobertura do
   *  rastreador — nunca confundir com 0, que é "mediu e não andou". Opcional
   *  porque payload anterior a esta versão não traz o campo. */
  distancia_m?: number | null
  distancia_qualidade?: 'boa' | 'parcial' | 'indisponivel' | null
  /** Jornada aberta ou cobertura incompleta: número ainda crescendo. */
  distancia_parcial?: boolean | null
  /** A MESMA distância distribuída pelas etapas exclusivas do dia. Dimensão da
   *  jornada, não parcela extra: as etapas somam o período coberto e nunca se
   *  somam ao total da jornada. */
  distancia_por_status?: Record<string, DistanciaEtapa> | null
}

/** Os cinco tipos de infração de jornada: interjornada menor que 11h, refeição
 *  fora do intervalo de 1 a 2h, direção contínua acima de 5h30 (Samuel,
 *  21/08/2026), desde 03/09/2026 o deslocamento do operador que encerra a
 *  jornada a mais de 10 km de onde abriu e, desde 04/09/2026, a disposição com
 *  hora extra: diária lançada como Disposição com jornada acima do teto do dia
 *  da semana, que era alerta de diária e virou infração de jornada. Espelha
 *  `jornada_infracoes.TIPOS_INFRACAO`. A quinta não se apura ao vivo. */
export type TipoInfracao =
  | 'interjornada' | 'refeicao' | 'direcao' | 'deslocamento' | 'disposicao_hora_extra'
  | 'trabalho_acima_12_horas'
  /** TPI e TPF acima de 1 hora em jornada com hora extra, de 22/09/2026. Só
   *  motorista e prancheiro, e ambas nascem no fechamento. */
  | 'tpi_hora_extra' | 'tpf_hora_extra'
  /** TPF e TPI acima de 1 hora na MESMA jornada, numa ocorrência só, de
   *  28/09/2026: "vamos evitar de ter duas infrações do mesmo motorista no mesmo
   *  dia". `valor_min` é a soma das duas bordas. */
  | 'tpi_tpf_hora_extra'
  /** Abriu jornada antes do programado, de 13/10/2026: só motorista, jornada
   *  aberta mais de 1 hora antes da hora programada da primeira carga da escala e
   *  menos de 15 km rodados até lá. `valor_min` é a antecedência e `valor_km` o
   *  percorrido. */
  | 'abertura_antecipada'

/** Uma ocorrência de infração. `valor_min` é o número que a pessoa entende: o
 *  intervalo entre jornadas, a refeição medida ou a corrida de direção. O
 *  deslocamento mede quilômetro e não minuto: `valor_min` nulo e `valor_km` no
 *  lugar. A janela fica na régua de minutos do dia âncora; `ini_min` negativo é
 *  a véspera, que é onde o Fim de Jornada anterior costuma estar. */
export interface JornadaInfracao {
  tipo: TipoInfracao
  valor_min: number | null
  valor_km?: number | null
  ini_min: number | null
  fim_min: number | null
}

/** Contagem de ocorrências por tipo, mais quantos colaboradores têm ao menos
 *  uma. Sempre com as cinco chaves. */
export interface JornadaInfracoesResumo {
  interjornada: number
  refeicao: number
  direcao: number
  deslocamento: number
  motoristas: number
}

/** Distância medida numa etapa. `distancia_m` null = sem cobertura (≠ 0). */
export interface DistanciaEtapa {
  distancia_m: number | null
  pontos_utilizados: number
  qualidade: 'boa' | 'parcial' | 'indisponivel'
  parcial: boolean
}

/** Jornada por etapa do Painel de Descarga, no dia. `por_status` é ESPARSO: só os
 *  status por onde o colaborador passou, tipicamente 2 a 4 dos 10 do catálogo. */
export interface JornadaPorStatus {
  /** Soma dos `jornada_min`: a jornada do dia distribuída pelas etapas. */
  total_min: number
  /** Relógio de parede coberto pela linha do tempo. */
  janela_min: number
  /** A linha do tempo veio do status atual, não de evento: presumida, não medida. */
  sem_evento: boolean
  /** O replay dos eventos terminou diferente do status que o Painel mostra agora. */
  divergente: boolean
  por_status: Record<string, JornadaNaDescarga>
}

/** Corrida de parado colada num marco de jornada. `min` é a duração cheia,
 *  `he_min` só o pedaço em hora extra — que é o que interessa ao cliente.
 *  `truncado` = a corrida encosta no piso da janela de 48h. */
export interface JornadaBorda {
  min: number
  he_min: number
  ini_min: number
  fim_min: number
  truncado: boolean
}

export interface JornadaBordas {
  abertura_min: number
  abertura_he_min: number
  encerramento_min: number
  encerramento_he_min: number
  /** MAIOR borda isolada do dia, e é ela que o ranking usa. Com duas jornadas no
   *  mesmo dia a soma não cabe dentro de nenhuma delas, e o card, que se chama
   *  "maior tempo parado após abrir", mostraria um número maior que a jornada da
   *  própria linha. Opcional porque payload anterior não traz o campo. */
  abertura_max_min?: number
  encerramento_max_min?: number
  jornadas: {
    ini_min: number
    fim_min: number | null
    aberta: boolean
    truncada_ini: boolean
    abertura: JornadaBorda | null
    /** null quando a jornada ainda está aberta: não há encerramento a medir. */
    encerramento: JornadaBorda | null
  }[]
}

/** `janela_min` é o relógio de parede no destino; `jornada_min` é quanto disso
 *  contou como jornada. A leitura é a razão entre os dois. */
export interface JornadaNaDescarga {
  jornada_min: number
  he_min: number
  janela_min: number
  por_tipo: Record<string, number>
}

/** Cronômetro de uma viagem. `fonte` diz de onde veio o número: `evento` é o
 *  histórico de status, `campo` é o carimbo antigo do shipment. `ubs_suspeita`
 *  marca carga presa ou saneada, que fica fora das médias. */
export interface ViagemTempos {
  carga: string
  trip_status: string | null
  destino: string | null
  ubs_min: number | null
  ubs_aberto: boolean
  ubs_fonte: 'evento' | 'campo' | null
  ubs_suspeita: boolean
  ubs_reaberta: boolean
  /** Encerrada junto com outras no mesmo instante: o operador baixou a fila. O
   *  fim não é o fim real da descarga, então fica fora da média. */
  ubs_lote: boolean
  transito_min: number | null
  transito_aberto: boolean
  transito_fonte: 'evento' | 'campo' | null
  /** Distância GPS das mesmas janelas, em metros. null = sem cobertura, que a
   *  tela mostra como travessão — nunca como 0,00 km. Opcionais: payload antigo
   *  não traz os campos. */
  transito_distancia_m?: number | null
  ubs_distancia_m?: number | null
}

export interface ResumoTempos {
  n: number
  total_min: number
  media_min: number
  mediana_min: number
  p90_min: number
  max_min: number
}

/** Analytics do dia do Painel de Jornada: blocos por motorista + totais da frota.
 *  Ordenado pelo início ativo (quem só dorme vai para o fim). */
export interface JornadaAnalytics {
  atualizado_em: string | null
  dia: string
  /** `dia` é o dia corrente. A Análise do dia não retroage mais (19/08/2026): dia
   *  encerrado se lê no Histórico, que responde outra pergunta. A chave fica porque
   *  o endpoint ainda aceita `?dia=`, e é ela que diz se há pulso ao vivo. */
  ao_vivo: boolean
  agora_min: number
  motoristas: JornadaMotoristaDia[]
  /** Denominadores da BASE, sobre a frota inteira e antes dos filtros da tela.
   *
   *  Existem para cada bloco escrever QUEM ele está contando. Até 18/08/2026 os três
   *  blocos partiam de recortes próprios e os números do mesmo dia não fechavam — a
   *  lista dizia 471, a linha do tempo desenhava 109 e o tempo por status contava
   *  321. Agora a população é uma só e a diferença entre os recortes fica escrita. */
  base: {
    /** Todo motorista e operador ATIVO no cadastro, dentro do escopo. */
    total: number
    /** Destes, quantos têm marcação da ATS registrada hoje. */
    com_marcacao: number
    /** E quantos têm ao menos uma jornada delimitada no dia. */
    com_jornada: number
    /** E quantos têm conjunto ativo, que é o que dá linha do tempo de status. */
    com_conjunto: number
  }
  totais: Record<string, number>
  /** Somas da frota do parado nas bordas da jornada. `motoristas_abertura` conta
   *  quem teve QUALQUER parado na borda; os `_he` só quem teve hora extra. */
  bordas: {
    abertura_min: number
    abertura_he_min: number
    encerramento_min: number
    encerramento_he_min: number
    motoristas_abertura: number
    motoristas_encerramento: number
    motoristas_abertura_he: number
    motoristas_encerramento_he: number
    /** Colaboradores que JÁ FECHARAM alguma jornada no dia.
     *
     *  Zero aqui não é frota parada zero: a jornada é encerrada em LOTE no dia
     *  seguinte, então na hora em que o painel mede ainda não existe encerramento
     *  nenhum. É o que separa "0min" de "indisponível até fechar". */
    jornadas_encerradas: number
  }
  /** Jornada da frota distribuída pelas etapas do Painel de Descarga. */
  status_operacional: {
    total_min: number
    /** Colaboradores com linha do tempo de status. */
    motoristas: number
    /** Destes, quantos vieram do status atual em vez de evento. */
    sem_evento: number
    /** E quantos terminaram o replay diferente do status de agora. */
    divergentes: number
    por_status: Record<string, {
      jornada_min: number
      he_min: number
      janela_min: number
      /** Quem teve jornada correndo neste status: o denominador honesto. */
      motoristas: number
    }>
  }
  /** Somas da frota da jornada dentro do Descarregando. */
  descarga: {
    jornada_min: number
    he_min: number
    janela_min: number
    motoristas: number
    suspeitas: number
  }
  /** Tempos de viagem. Vem do banco, então sobrevive à ATS fora do ar. */
  viagens: {
    ubs: ResumoTempos & { abertas: number; suspeitas: number; lote: number; sem_dado: number }
    transito: ResumoTempos & { abertas: number; sem_dado: number }
    sem_motorista: number
  }
  /** Ocorrências de infração da frota, sobre as jornadas abertas hoje. */
  infracoes: JornadaInfracoesResumo
}

/** Uma linha do Histórico do Painel de Jornada: um colaborador num dia encerrado.
 *
 *  O contexto (função, base, dupla, cliente, veículo) é o que valia NAQUELE dia,
 *  congelado no fechamento — nenhum desses vínculos tem histórico no cadastro, e
 *  resolvê-los na leitura faria uma troca de dupla hoje reescrever o passado. */
export interface JornadaHistoricoLinha {
  uuid: string
  /** Data-ÂNCORA: o dia do Início de Jornada, mesmo que ela termine no seguinte. */
  dia: string
  /** Ordem da jornada dentro do dia. 0 = dia sem jornada, que existe só para a
   *  classificação — Folga, DSR, Feriado — continuar contando nos indicadores. */
  seq: number
  /** Sem Fim de Jornada: número parcial, não somar com jornada encerrada. */
  aberta: boolean
  /** Havia fim sem início, ou status fora de toda jornada. O tempo desses registros
   *  fica FORA dos totais; a marca existe para a tela poder dizer isso. */
  inconsistente: boolean
  /** Classificação do dia pela ATS: Trabalho, Folga, Feriado, DSR, À Disposição.
   *  Até 04/09/2026 era a coluna "Diária"; continua aqui para quem a consultar.
   *  null = a ATS não classificou aquele dia. */
  status_dia: string | null
  /** A coluna Diária desde 04/09/2026 (Samuel): a diária que vale no Painel de
   *  Diárias para a pessoa no dia, o lançamento do HUB por cima do espelho do
   *  ERP, com o tipo e quem lançou. null = ninguém lançou nada. */
  diaria: string | null
  diaria_tipo: string | null
  diaria_usuario: string | null
  /** De onde a linha saiu. `marcacao` é o caminho normal, pelas batidas.
   *  `historico` é o dia que a ATS classificou SEM devolver batida nenhuma: os
   *  tempos vêm todos zerados e a tela precisa dizer isso, senão a ausência de
   *  ponto passa por jornada de duração zero. */
  fonte: 'marcacao' | 'historico'
  motorista_uuid: string
  nome: string
  /** Do CADASTRO, resolvidas na leitura, não congeladas na linha: o relatório é
   *  conferido contra a folha, então vale a lotação e o número de hoje. */
  matricula: string | null
  filial: string | null
  funcao: string | null
  base: string | null
  dupla: string | null
  cliente: string | null
  veiculo: string | null
  jornada_liquida_min: number | null
  /** Tempo em direção no dia. */
  direcao_min: number
  /** Tempo parado colado na abertura da jornada. */
  tpi_min: number
  /** Tempo parado colado no encerramento. Zero na jornada que não fechou. */
  tpf_min: number
  parado_total_min: number
  /** A hora extra que a ATS MARCOU, desde 22/09/2026: o HUB não calcula quanto
   *  foi extra, replica. O total e cada uma das duas marcações que o produzem, 122
   *  Direção - Hora Extra e 156 Parado - Hora Extra; direção mais parado é o total. */
  hora_extra_min: number
  hora_extra_direcao_min: number
  hora_extra_parado_min: number
  /** O MESMO total classificado pelo dia da abertura: domingo e feriado a 100%,
   *  os demais dias a 50%. É a única parte da regra que ficou no HUB, porque a ATS
   *  não informa percentual. 50% mais 100% é sempre `hora_extra_min`. */
  he50_min: number
  he100_min: number
  alerta: JornadaAlerta
  inicio_jornada: string | null
  fim_jornada: string | null
  /** Onde a ATS marcou o colaborador no marco de início e no de encerramento —
   *  posição da PRÓPRIA batida, não a última conhecida. */
  local_inicio: string | null
  local_fim: string | null
  /** Coordenada da MESMA batida do texto acima, quando ela veio com posição. O
   *  texto da ATS é uma referência relativa ("11.46 Km de Posto X") e não localiza
   *  no mapa; é este par que sustenta o atalho para o Google Maps. Jornada fechada
   *  antes de 14/08/2026 não tem o par até o dia ser refeito por `jornada-backfill`. */
  local_inicio_lat: number | null
  local_inicio_lng: number | null
  local_fim_lat: number | null
  local_fim_lng: number | null
  /** Distância GPS da jornada em METROS, medida sobre o histórico do rastreador
   *  e congelada no fechamento. null = não foi possível medir (linha anterior à
   *  coleta, veículo ambíguo, sem cobertura); 0 = mediu e não andou. Opcionais:
   *  payload anterior a esta versão não traz os campos. */
  distancia_m?: number | null
  distancia_qualidade?: 'boa' | 'parcial' | 'indisponivel' | null
  distancia_pontos?: number | null
  distancia_cobertura_seg?: number | null
  /** Motivo técnico quando indisponível: sem_conjunto, conjunto_ambiguo,
   *  sem_veiculo, veiculo_sem_fleet, sem_pontos, cobertura_insuficiente,
   *  sem_jornada. */
  distancia_motivo?: string | null
}

/** Um dia de feriado, válido para a operação inteira.
 *
 *  Não é enfeite de calendário: a data alimenta a régua de hora extra do Painel de
 *  Jornada, onde feriado vale como domingo e leva a jornada inteira daquele dia a
 *  100%. Gravar ou apagar recalcula o dia, inclusive em jornada já fechada. */
/** Cargo da equipe (Samuel, 15/10/2026): Programador, Assistente. O operador com
 *  cargo herda estas permissões, e mudá-las aqui muda todos os usuários dele. */
export interface Cargo {
  uuid: string
  nome: string
  permissions: string[]
  /** As áreas que o cargo libera (24/10/2026). A permissão de um módulo só vale
   *  com a área dele concedida. */
  areas: string[]
  /** Usuários neste cargo. */
  usuarios: number | null
}

export interface Feriado {
  uuid: string
  /** AAAA-MM-DD. Casa com o dia em que a JORNADA ABRIU, não com o dia civil em que
   *  os minutos caíram: jornada aberta na véspera segue a régua da véspera. */
  dia: string
  nome: string
}

/** Somas sobre o conjunto FILTRADO, não sobre a página. */
export interface JornadaHistoricoIndicadores {
  dias: number
  trabalho: number
  folga: number
  disposicao: number
  /** Feriado, DSR e o que mais a ATS classificar fora das três categorias. */
  outros: number
  /** Dias que a ATS não classificou: ausência de dado, não categoria. */
  sem_classificacao: number
  he_ate4: number
  he_acima4: number
  parado_total_min: number
  tpi_min: number
  tpf_min: number
  /** Quanto do parado é borda de jornada. null sem parado — zero afirmaria que
   *  nada foi borda, o que é diferente de não haver proporção. */
  tpi_pct: number | null
  tpf_pct: number | null
  /** Soma das distâncias medidas no conjunto filtrado, em metros. null quando
   *  nenhuma linha tem medida — não é zero. */
  distancia_total_m?: number | null
  /** O TOTAL das horas válidas do período, a soma da coluna Jornada. */
  trabalhadas_min: number
  /** A hora extra da ATS no período: as duas marcações, o total, e a classificação
   *  do mesmo total em 50% e 100%. Direção mais parado e 50% mais 100% dão ambas o
   *  total, e nunca se somam entre si. */
  hora_extra_direcao_min: number
  hora_extra_parado_min: number
  hora_extra_min: number
  he50_min: number
  he100_min: number
}

/** Os blocos da Análise do dia sobre o PERÍODO filtrado (Samuel, 21/08/2026):
 *  rankings por colaborador, cartões de tempo parado e tempo por status
 *  operacional. Tudo sobre o conjunto filtrado, nunca sobre a página. Filtrar por
 *  um colaborador reduz tudo a ele; sem filtro é o consolidado. */
export interface JornadaHistoricoAgregados {
  por_colaborador: {
    motorista_uuid: string
    nome: string
    /** Linhas com jornada de verdade no período, seq ≥ 1. */
    jornadas: number
    direcao_min: number
    /** A hora extra da ATS somada no período, o total e cada marcação. É por elas
     *  que os três rankings de hora extra ordenam desde 22/09/2026. */
    hora_extra_min: number
    hora_extra_direcao_min: number
    hora_extra_parado_min: number
    he50_min: number
    he100_min: number
    parado_total_min: number
    /** MAIOR borda isolada do período, pelo mesmo motivo do dia: o card se
     *  chama "maior tempo parado após abrir", e a soma de trinta jornadas não
     *  cabe dentro de nenhuma delas. */
    tpi_max_min: number
    tpf_max_min: number
  }[]
  /** As mesmas somas por pessoa, só com os dias em que ela foi Motorista ou
   *  Prancheiro. São elas que os rankings de tempo e de hora extra por marcação
   *  ordenam, desde 30/09/2026; Quem mais fez hora extra segue em `por_colaborador`. */
  por_motorista_e_prancheiro: Pick<JornadaHistoricoAgregados['por_colaborador'][number],
    'motorista_uuid' | 'nome' | 'direcao_min' | 'hora_extra_direcao_min' | 'hora_extra_parado_min'
    | 'parado_total_min' | 'tpi_max_min' | 'tpf_max_min'>[]
  bordas: {
    abertura_min: number
    abertura_he_min: number
    encerramento_min: number
    encerramento_he_min: number
    motoristas_abertura: number
    motoristas_encerramento: number
    /** Jornadas com Fim de Jornada no período. */
    jornadas_encerradas: number
  }
  descarga: {
    jornada_min: number
    janela_min: number
    /** Quem teve ao menos uma jornada com viagem no período. */
    motoristas: number
    suspeitas: number
  }
  status_operacional: {
    motoristas: number
    por_status: Record<string, {
      jornada_min: number
      he_min: number
      janela_min: number
      motoristas: number
    }>
    por_colaborador: {
      motorista_uuid: string
      nome: string
      total_min: number
      por_status: Record<string, { jornada_min: number; he_min: number; janela_min: number }>
    }[]
  }
}

/** Uma linha do Relatório de Infrações: uma OCORRÊNCIA (Samuel, 21/08/2026).
 *  Três infrações do mesmo motorista no mesmo dia são três linhas. */
export interface JornadaInfracaoLinha {
  uuid: string
  /** A jornada dona da ocorrência, em `jornada_dia`: é com ele que a linha abre
   *  o dialog de batidas do Histórico, sem endpoint novo. */
  registro_uuid: string
  /** Dia âncora da jornada, o do Início. */
  dia: string
  seq: number
  motorista_uuid: string
  nome: string
  /** Filial CORRENTE do cadastro: não existe congelada em lugar nenhum. */
  filial: string | null
  /** Função e base congeladas no fechamento, como no Histórico. */
  funcao: string | null
  base: string | null
  /** As colunas de 04/09/2026 (Samuel, enquanto monta as metas): a dupla e a
   *  jornada do dia vêm do fechamento; a diária, o tipo e quem lançou vêm do
   *  Painel de Diárias. */
  dupla: string | null
  jornada_liquida_min: number | null
  /** As colunas da jornada dona, de 28/09/2026 (Samuel): o veículo, os três marcos
   *  com a posição para o atalho do mapa, a distância e a hora extra. São as do
   *  Histórico, lidas do mesmo registro. */
  veiculo: string | null
  inicio_jornada: string | null
  local_inicio: string | null
  local_inicio_lat: number | null
  local_inicio_lng: number | null
  refeicao_inicio: string | null
  refeicao_lat: number | null
  refeicao_lng: number | null
  fim_jornada: string | null
  local_fim: string | null
  local_fim_lat: number | null
  local_fim_lng: number | null
  distancia_m: number | null
  distancia_qualidade: string | null
  /** O total da hora extra da ATS, e a classificação dele em 50% e 100%. */
  hora_extra_min: number | null
  he50_min: number | null
  he100_min: number | null
  diaria: string | null
  diaria_tipo: string | null
  diaria_usuario: string | null
  tipo: TipoInfracao
  /** Minutos, nulo no deslocamento. */
  valor_min: number | null
  /** Só no deslocamento: km em linha reta entre a abertura e o encerramento. */
  valor_km: number | null
  ini_min: number | null
  fim_min: number | null
  /** Só na refeição tardia, de 23/10/2026: quanto de jornada líquida havia quando a
   *  refeição começou, acima de 6h. Ela pode ter duração normal, e é isto que a
   *  explica. Nulo nos outros casos. */
  refeicao_apos_min?: number | null
  /** Só na ocorrência de TPF e TPI, de 28/09/2026: cada borda da jornada dona, que
   *  a linha escreve embaixo do rótulo. `valor_min` é a soma das duas. */
  tpi_min?: number | null
  tpf_min?: number | null
  /** Tempo parado com o número da ATS, sem a confirmação do rastreador: jornada sem
   *  veículo identificado ou sem posição perto da ponta, de 16/09/2026 em diante. */
  sem_rastreador?: boolean
  /** A programação do dia da infração (Samuel, 13/10/2026): a escala da Frente para
   *  o motorista, a programação de colheita para o operador. Nulos sem programação,
   *  que a tela escreve como Sem escala. */
  programado_para: string | null
  cliente_alocado: string | null
  programacao_origem: 'escala' | 'colheita' | null
  programacao_contrato: string | null
  /** O pivô do contrato da programação, para o atalho do Google Maps ao lado da
   *  data e hora (27/10/2026). Nulos sem programação, sem contrato ou sem coordenada. */
  pivo_lat?: number | null
  pivo_lng?: number | null
  programacoes_no_dia: number
  /** A decisão de gente sobre a ocorrência, com quem e quando. */
  tratativa: 'pendente' | Tratativa
  justificativa: string | null
  tratado_por: string | null
  tratado_em: string | null
}

/** GET /dashboard/backoffice/jornada/infracoes — página + indicadores + rankings. */
export interface JornadaInfracoesBoard {
  itens: JornadaInfracaoLinha[]
  total: number
  page: number
  per_page: number
  pages: number
  de: string
  ate: string
  /** Sobre o conjunto FILTRADO, não sobre a página. */
  indicadores: {
    total: number
    interjornada: number
    refeicao: number
    direcao: number
    deslocamento: number
    disposicao_hora_extra: number
    trabalho_acima_12_horas: number
    tpi_hora_extra: number
    tpf_hora_extra: number
    tpi_tpf_hora_extra: number
    abertura_antecipada: number
    /** Colaboradores distintos com ao menos uma ocorrência. */
    colaboradores: number
    /** Quantas esperam gente, quantas foram justificadas, quantas viraram advertência. */
    tratativas: Record<'pendente' | Tratativa, number>
  }
  /** Ocorrências por colaborador, somando os quatro tipos e cada um à parte. */
  por_colaborador: {
    motorista_uuid: string
    nome: string
    total: number
    interjornada: number
    refeicao: number
    direcao: number
    deslocamento: number
    disposicao_hora_extra: number
    trabalho_acima_12_horas: number
    tpi_hora_extra: number
    tpf_hora_extra: number
    tpi_tpf_hora_extra: number
    abertura_antecipada: number
  }[]
  opcoes: {
    filiais: string[]
    funcoes: string[]
    bases: string[]
    tipos: TipoInfracao[]
    duplas: string[]
    tratativas: ('pendente' | Tratativa)[]
  }
}

// ── Gestões ───────────────────────────────────────────────────────────────────

/** De onde a ocorrência veio: a conferência de diária ou o relatório de infrações. */
export type OrigemAdvertencia = 'diaria' | 'jornada'

/** Uma ocorrência que alguém classificou como Gestão, onde ela vive. */
export interface AdvertenciaItem {
  origem: OrigemAdvertencia
  uuid: string
  dia: string
  personnel_uuid: string
  nome: string
  matricula: string | null
  filial: string | null
  base: string | null
  funcao: string | null
  /** O tipo do alerta ou da infração, no vocabulário de cada origem. */
  tipo: TipoAlertaDiaria | TipoInfracao
  detalhe: Record<string, number | string | boolean | null>
  justificativa: string | null
  registrado_por: string | null
  registrado_em: string | null
  status: 'pendente' | 'tratado'
  gestao_uuid: string | null
  gestao_codigo: string | null
  gestoes_anteriores: { uuid: string, codigo: string, status: string }[]
  /** A infração classificada que saiu da base, quase sempre porque a ATS corrigiu o
   *  ponto, e que o Relatório lê do retrato da aba Infrações (29/10/2026). */
  corrigida?: boolean
  /** Os marcos e o Tempo de Jornada da jornada da ocorrência (23/10/2026). Nulos na
   *  diária e na ocorrência que já não existe na base, e opcionais porque o retrato
   *  guardado antes de 23/10/2026 não os tem. */
  jornada_inicio?: string | null
  jornada_fim?: string | null
  jornada_total_min?: number | null
  /** A linha da aba Horas Indevidas que a ocorrência já tem, automática ou incluída
   *  à mão, ou null quando ainda não entrou. */
  hora_indevida?: { origem: 'automatica' | 'manual', incluido_por: string | null, incluido_em: string | null } | null
  /** O mínimo de jornada do domingo e do feriado, em minutos; null no dia comum. */
  piso_min?: number | null
}

/** Uma linha da lista operacional: só quem tem ocorrência pendente de tratativa,
 *  e só a quantidade de pendentes (08/10/2026). */
export interface AdvertenciaColaborador {
  personnel_uuid: string
  nome: string
  matricula: string | null
  base: string | null
  funcao: string | null
  pendentes: number
}

/** Uma posição de ranking de gestões por colaborador. */
export interface RankingGestaoColaborador {
  personnel_uuid: string
  nome: string
  funcao: string | null
  base: string | null
  total: number
}

/** GET /advertencias — o Relatório de Ocorrências: as duas origens lado a lado e a
 *  lista de quem tem pendente. O ranking geral saiu em 23/10/2026. */
export interface AdvertenciaBoard {
  itens: AdvertenciaItem[]
  total: number
  de: string
  ate: string
  por_colaborador: AdvertenciaColaborador[]
  opcoes: {
    origens: OrigemAdvertencia[]
    bases: string[]
    filiais: string[]
    /** Os tipos de ocorrência da lista, com a origem, sem o próprio filtro. */
    tipos: { origem: OrigemAdvertencia, tipo: AdvertenciaItem['tipo'] }[]
    /** As funções do cadastro de quem está na lista, sem o próprio filtro. */
    funcoes: { value: string, label: string }[]
  }
}

/** GET /dashboard/backoffice/jornada/historico — página + agregado + opções. */
export interface JornadaHistoricoBoard {
  itens: JornadaHistoricoLinha[]
  total: number
  page: number
  per_page: number
  pages: number
  de: string
  ate: string
  indicadores: JornadaHistoricoIndicadores
  agregados: JornadaHistoricoAgregados
  /** Valores presentes na JANELA de dias, não no filtro corrente: escolher um
   *  cliente não pode apagar os outros da lista. */
  opcoes: {
    bases: string[]
    funcoes: string[]
    clientes: string[]
    duplas: string[]
    status_dia: string[]
  }
}

// ── Painel de Diárias ───────────────────────────────────────────────────────

/** O que o ERP lançou. `null` na linha quer dizer que o Protheus não tem nada
 *  naquele dia para aquela pessoa, e é metade do que a tela existe para mostrar. */
export interface DiariaErp {
  status: string | null
  tipo_disposicao: string | null
  valor: number | null
  observacao: string | null
  /** A dupla NAQUELE dia, que muda ao longo do período. O HUB só conhece a
   *  corrente, então este é dado que não existe em nenhuma outra tela. */
  grupo_programacao: string | null
  codigo_dupla: string | null
  dupla: string | null
  usuario: string | null
  /** Sem usuário por trás: o ERP lançou sozinho. Não é dado faltando. */
  automatico: boolean
  lancado_em: string | null
  trabalhou: string | null
  afastado: string | null
  disposicao: string | null
}

/** O que o HUB decidiu. Nasce `rascunho` e vira `enviado` quando o ERP confirma.
 *  `origem` separa a decisão de uma pessoa da PREVISÃO automática da rotina, que
 *  responde `HUB` em `usuario` e `previsto` verdadeiro. */
export interface DiariaHub {
  status: string
  tipo_disposicao: string | null
  valor: number | null
  observacao: string | null
  estado: 'rascunho' | 'enviado'
  usuario: string | null
  origem: 'usuario' | 'hub'
  previsto: boolean
  lancado_em: string | null
}

/** A jornada apurada do dia, somada quando a pessoa teve mais de uma. Os três
 *  marcos, com hora e posição do MESMO marco, são da primeira jornada do dia. */
export interface DiariaJornada {
  status_dia: string | null
  direcao_min: number | null
  jornada_min: number | null
  parado_min: number | null
  /** Metros. `null` é não medido; `0` é medido e parado. */
  distancia_m: number | null
  /** Metros: a maior distância à garagem da base durante a jornada. `null` sem
   *  garagem cadastrada ou sem posição. */
  afastamento_max_m: number | null
  base: string | null
  dupla: string | null
  veiculo: string | null
  cliente: string | null
  local_inicio: string | null
  local_fim: string | null
  jornadas: number
  /** Há linha com `seq >= 1`: a pessoa de fato abriu jornada no dia. */
  com_jornada: boolean
  inicio: string | null
  inicio_lat: number | null
  inicio_lng: number | null
  refeicao_inicio: string | null
  refeicao_lat: number | null
  refeicao_lng: number | null
  fim: string | null
  fim_lat: number | null
  fim_lng: number | null
}

/** O que o HUB preveria para a linha pela jornada, ou `null` quando não há como.
 *
 *  `DISPOSICAO_GARAGEM` é a regra de 22/09/2026, só para operador: as três batidas do
 *  dia dentro de 5 km de qualquer garagem. As outras três medem deslocamento de
 *  veículo, que operador não tem. */
export type DiariaPrevisao =
  | 'TRABALHO' | 'DISPOSICAO_FORA' | 'DISPOSICAO_DENTRO' | 'DISPOSICAO_GARAGEM' | 'FOLGA'

/** Uma linha do painel: um colaborador num dia.
 *
 *  A linha NÃO existe no banco. Ela é derivada do cruzamento dos dias do período
 *  com a população de colaboradores, e por isso a identidade é o par pessoa mais
 *  dia, em `id`, e não um uuid de registro. */
export interface DiariaLinha {
  /** `<personnel_uuid>:<dia>`. É o `rowKey` da grade e o que a escrita manda. */
  id: string
  dia: string
  personnel_uuid: string
  nome: string
  matricula: string | null
  filial: string | null
  /** Função pela VIGÊNCIA na data, não a de hoje. */
  funcao: string | null
  /** O código da mesma função: `motorista`, `manobra`, `prancheiro`, `operador`. */
  funcao_codigo: string | null
  /** Base da PESSOA no cadastro do ERP, a mesma que o fechamento congela na jornada.
   *  Existe também no dia sem jornada e no futuro, ao contrário de `jornada.base`. */
  base: string | null
  /** Município de origem da pessoa, o de residência no cadastro do ERP. O mesmo
   *  da coluna Município origem do Painel de Descarga. */
  municipio: string | null
  /** A dupla em que a pessoa está CADASTRADA no HUB, fonte do filtro Dupla. A dupla
   *  naquele dia segundo o ERP continua em `erp.grupo_programacao`. Mais de uma
   *  dupla vem separada por vírgula. */
  dupla: string | null
  /** A dupla do dia é CONTROLE - FUNCIONARIOS OCIOSOS: o grupo do ERP naquele dia,
   *  e o cadastro quando o ERP não mandou nada. Lido pelo alerta Ocioso com jornada. */
  em_controle_ociosos: boolean
  /** Há quantos dias a linha espera decisão: ontem 1, anteontem 2, antes 3. Hoje
   *  e o futuro são 0. */
  alerta_dias: 0 | 1 | 2 | 3
  previsao: DiariaPrevisao | null

  /** Registro oficial do Protheus. O rascunho permanece em `hub`. */
  status: string | null
  tipo_disposicao: string | null
  valor: number | null
  observacao: string | null
  /** Responsável pelo lançamento: quem informou no HUB quando a diária do ERP é o
   *  rascunho subido pelo CSV, HUB para a previsão, e o autor do ERP fora disso. */
  usuario: string | null
  /** `usuario` veio do HUB; quem subiu o CSV continua em `erp.usuario`. */
  informado_no_hub: boolean
  /** Data do lançamento no Protheus em formato ISO. */
  lancado_em: string | null
  origem: 'hub' | 'erp' | 'vazio'

  erp: DiariaErp | null
  hub: DiariaHub | null
  jornada: DiariaJornada | null
}

export interface DiariaIndicadores {
  linhas: number
  trabalho: number
  folga: number
  disposicao: number
  /** Só o dia em branco nos dois lados. Serve à aba Lançamento. */
  sem_lancamento: number
  /** Dia em branco OU status DISPONIVEL do ERP: para o Samuel são a mesma coisa. */
  disponivel: number
  /** Status INCONSISTENTE do ERP, a origem admitindo o próprio erro. */
  inconsistentes: number
  do_hub: number
  rascunhos: number
  /** Linhas decididas pela previsão automática do HUB. */
  previstos: number
  /** Disponível há um dia, dois dias, três ou mais: os alertas do Lançamento. */
  alerta_1: number
  alerta_2: number
  alerta_3_mais: number
  valor_total: number | null
  /** Uma contagem por status conhecido, com o vazio somado em DISPONIVEL e o que a
   *  tela não conhece em OUTROS. Alimenta a pizza. */
  por_status: Record<string, number>
}

export interface DiariaDuplaOpcao {
  uuid: string
  nome: string
}

/** Quem decidiu rascunho, no filtro Responsável do Lançamento. Casa pelo uuid,
 *  como a dupla: nome de usuário não é único. */
export interface DiariaResponsavelOpcao {
  uuid: string
  nome: string
}

export interface DiariaDisponiveisResumo {
  alcance: { de: string, ate: string }
  periodos: Array<{ periodo: string, de: string, ate: string, total: number }>
  duplas: Array<{ dupla: string | null, total: number }>
  dias: Array<{ dia: string, total: number }>
}

/** Uma posição nos rankings de sequência: quantos dias e desde quando. */
export interface DiariaSequenciaLinha {
  personnel_uuid: string
  nome: string
  valor: number
  desde: string | null
}

/** Uma dupla do cadastro com a situação de HOJE dos seus colaboradores. `nome`
 *  nulo é a linha de quem não está em dupla nenhuma. */
export interface DiariaDuplaHoje {
  uuid: string | null
  nome: string | null
  total: number
  trabalho: number
  folga: number
  disponivel: number
  outros: number
}

/** GET /diarias/rankings — as três sequências, ancoradas no fim do período sem passar
 *  de hoje, e a situação de hoje por dupla. */
export interface DiariaRankings {
  ancora: string
  hoje: string
  sequencias: {
    domingos: DiariaSequenciaLinha[]
    dias_trabalhados: DiariaSequenciaLinha[]
    folgas: DiariaSequenciaLinha[]
  }
  duplas_hoje: DiariaDuplaHoje[]
}

/** GET /diarias/painel — página, indicadores do conjunto filtrado e opções. */
export interface DiariaPainelBoard {
  itens: DiariaLinha[]
  total: number
  page: number
  per_page: number
  pages: number
  de: string
  ate: string
  /** Onde está o corte entre conferir e planejar: daqui para a frente a linha é
   *  decisão a tomar, e a conferência não emite veredito. */
  hoje: string
  indicadores: DiariaIndicadores
  opcoes: {
    status: string[]
    tipos_disposicao: string[]
    funcoes: string[]
    /** Responsáveis pelo lançamento no período, do HUB e do ERP. O lançamento
     *  automático não está aqui: a tela oferece a opção fixa. */
    usuarios: string[]
    bases: string[]
    /** Duplas do cadastro com gente no período. O filtro casa pelo uuid. */
    duplas: DiariaDuplaOpcao[]
    filiais: string[]
    /** Bases do quadro sem garagem cadastrada: nelas a previsão não tem de onde
     *  medir o raio, e a tela avisa. */
    bases_sem_garagem: string[]
    /** Quem decidiu rascunho no universo do Lançamento, para o filtro Responsável
     *  (Samuel, 24/09/2026). HUB e Sem lançamento a tela acrescenta; fora do
     *  Lançamento vem vazia. */
    responsaveis: DiariaResponsavelOpcao[]
  }
  /** Quando o espelho do ERP foi confirmado pela última vez. Sem esta marca, quem
   *  não acha o lançamento recém-feito no Protheus não sabe se o problema é a
   *  espera ou a integração. */
  sincronizado_em: string | null
  /** Valores de situação e de tipo que a origem usa e a tela não conhece.
   *
   *  A grade os mostra como vieram de qualquer jeito, porque o espelho guarda o
   *  texto cru. O que esta lista resolve é ninguém SABER que eles são novos: o
   *  relatório do ciclo já denuncia, mas vive em log. Aqui o aviso aparece na cara
   *  de quem confere, e importa mais nesta tela que nas outras porque estes dois
   *  campos alimentam a conferência. */
  vocabulario_novo: string[]
  /** Presente no Painel de Lançamento. Períodos ignoram o recorte de datas;
   *  dupla e dia respeitam todos os filtros da tela. */
  disponiveis?: DiariaDisponiveisResumo
}

// ── Painel de Conferência ───────────────────────────────────────────────────

/** Os alertas da conferência (Samuel, 03/09/2026), na ordem de severidade.
 *  `disposicao_com_hora_extra` saiu da conferência em 04/09/2026 e virou infração
 *  de jornada; fica no tipo só para os alertas antigos já tratados continuarem
 *  legíveis no módulo de Gestões. `ociosos_com_jornada` entrou em 15/10/2026: há
 *  jornada no dia e a dupla do dia é CONTROLE - FUNCIONARIOS OCIOSOS. */
export type TipoAlertaDiaria =
  | 'folga_com_jornada'
  | 'falta_com_jornada'
  | 'suspensao_com_jornada'
  | 'ferias_com_jornada'
  | 'afastamento_com_jornada'
  | 'ociosos_com_jornada'
  | 'trabalho_incorreto'
  | 'disposicao_incorreta'
  | 'lancamento_inconsistente'
  | 'diaria_duplicada'
  | 'disposicao_com_hora_extra'

/** Nasce pendente; a rotina corrige quando a causa some; uma pessoa justifica. */
export type StatusConferencia = 'pendente' | 'corrigido' | 'justificado' | 'advertencia'

/** O que uma pessoa pode decidir sobre uma ocorrência (Samuel, 03/09/2026):
 *  justificar, com o motivo por extenso, ou classificar como Gestão, que a leva
 *  para o módulo de Gestões. O valor `advertencia` é anterior ao nome Gestão, de
 *  08/10/2026. Vale para o alerta de diária e para a infração de jornada. */
export type Tratativa = 'justificado' | 'advertencia'

/** Um valor do vocabulário de diária com o código que o Protheus espera no CSV.
 *  `codigo` é o que vale, gravado na tela ou ditado pelo cliente; `padrao` é o
 *  ditado; nulo é sem código, e desde 22/09/2026 o CSV que o encontra é recusado. */
export interface CodigoErpItem {
  valor: string
  codigo: string | null
  padrao: string | null
  /** Só nos tipos: a diária a que o tipo pertence. */
  status?: string
}

export interface CodigosErp {
  status: CodigoErpItem[]
  tipos: CodigoErpItem[]
}

/** Um alerta: uma linha permanente. Diária, tipo, jornada e distância saem AO
 *  VIVO; `retrato` é o que o HUB viu no instante da detecção. */
export interface DiariaAlertaLinha {
  uuid: string
  dia: string
  personnel_uuid: string
  nome: string
  matricula: string | null
  filial: string | null
  funcao: string | null
  base: string | null
  dupla: string | null
  cliente: string | null
  status: string | null
  tipo_disposicao: string | null
  usuario: string | null
  veiculo: string | null
  jornada_min: number | null
  distancia_m: number | null
  tipo: TipoAlertaDiaria
  status_conferencia: StatusConferencia
  justificativa: string | null
  justificado_por: string | null
  justificado_em: string | null
  detectado_em: string
  corrigido_em: string | null
  retrato: {
    diaria_lancada: string | null
    tipo_lancado: string | null
    previsao: DiariaPrevisao | null
    jornada_min: number | null
    distancia_m: number | null
    afastamento_max_m: number | null
    /** Só na disposição com hora extra: o teto que valia naquele dia da semana. */
    limite_min: number | null
    /** Nos dois alertas da carga crua do ERP, os lançamentos; no ocioso com
     *  jornada, a dupla do dia na detecção. */
    origem_detalhes: {
      quantidade?: number
      lancamentos?: Array<Record<string, string | null>>
      dupla_do_dia?: string | null
    } | null
  }
}

/** GET /diarias/conferencia — página de alertas, indicadores e opções. */
export interface DiariaConferenciaBoard {
  itens: DiariaAlertaLinha[]
  total: number
  page: number
  per_page: number
  pages: number
  de: string
  ate: string
  hoje: string
  /** A partir de quando as conferências rodam. */
  inicio: string
  indicadores: {
    total: number
    pendente: number
    corrigido: number
    justificado: number
    advertencia: number
  }
  opcoes: {
    tipos: TipoAlertaDiaria[]
    status: StatusConferencia[]
    funcoes: string[]
    bases: string[]
    filiais: string[]
    duplas: DiariaDuplaOpcao[]
  }
}

/** POST /diarias/conferencia/reavaliar — o relatório da rodada. */
export interface DiariaConferenciaRelatorio {
  de: string
  ate: string
  dias: number
  dias_sem_fechamento: number
  linhas: number
  avaliados: number
  criados: number
  corrigidos: number
  mantidos: number
  por_tipo: Record<string, number>
  commit: boolean
}

export interface PainelDescargaRow {
  conjunto_uuid: string
  name: string | null
  dupla_uuid: string | null
  dupla_name?: string | null
  driver: string | null
  /** Filial e matrícula do motorista, "0102-000158", mostradas depois do nome. */
  driver_matricula?: string | null
  /** Município do CADASTRO do motorista, de onde ele sai. Não é a posição de agora. */
  municipio_origem?: string | null
  /** Base do ERP já sem o nome da empresa: "TRANSGRAOS - FORMOSA" chega "FORMOSA". */
  base?: string | null
  /** Jornada do motorista na ATS (tempo real). null = sem match / poller off. */
  jornada?: PainelJornada | null
  truck_plate: string | null
  trailer1_plate: string | null
  trailer2_plate: string | null
  /** Tipo de cada veículo do conjunto, para o ícone junto da placa. */
  truck_category: Categoria | null
  trailer1_category: Categoria | null
  trailer2_category: Categoria | null
  status: DescargaStatus | string
  status_derivado: DescargaStatus | string
  /** Último dia da Folga, "AAAA-MM-DD", quando o operador disse até quando. No dia
   *  seguinte o conjunto volta a Disponível sozinho (28/09/2026). */
  folga_ate?: string | null
  /** Último Disponível do veículo: a última vez que ele ficou vazio depois de uma
   *  operação. Vem em QUALQUER status desde 22/09/2026, e null é nunca registrado. */
  available_at: string | null
  /** Momento ISO em que entrou no status atual. */
  status_since: string | null
  /** A entrada em Descarregando corrigida no módulo de Viagens, só na linha em
   *  Descarregando que tem a correção. É o piso do diálogo "Quando ficou
   *  disponível?" no lugar de `status_since` (27/10/2026). */
  descarregando_corrigido_em?: string | null
  /** Momento ISO do escalonamento que abriu o ciclo atual (null fora das etapas de viagem). */
  escalado_em?: string | null
  position: number | null
  local_descarga: string | null
  /** Destino efetivo, incluindo os fallbacks da carga e do contrato. */
  local_efetivo?: string | null
  /** Última localização válida do rastreador, limitada a 24 horas. A coordenada
   *  alimenta o atalho do Google Maps da Frente. */
  local_atual?: { municipio: string; uf: string | null; msg_dt: string; lat?: number | null; lon?: number | null } | null
  sml: string | null
  notes: string | null
  /** Dia da escala na Frente de Trabalho, "AAAA-MM-DD", sem a hora. Nulo quando a
   *  linha não herda carga com contrato (28/09/2026). */
  data_carregamento?: string | null
  shipment: { uuid: string; number: string; trip: string | null; status: ShipmentStatus; trip_status?: string | null; destination?: string | null; contract: string | null; modality?: string | null } | null
  /** A última macro VEICULO VAZIO do cavalo depois da entrada em Descarregando
   *  (19/10/2026). Só vem na linha em Descarregando; null sem macro. */
  macro_vazio?: PainelMacroVazio | null
  /** Quantas gestões pendentes o motorista da linha tem no módulo de Gestões, em
   *  qualquer status (27/10/2026). Zero sem gestão. A coluna Alerta mostra GESTÃO. */
  gestoes_pendentes?: number
  /** Indicadores do motorista da linha nos últimos 90 dias (22/10/2026). Conjunto
   *  sem motorista vem com os quatro em zero. */
  indicadores_motorista?: PainelIndicadoresMotorista
}

/** Os quatro números do botão + do Painel de Descarga: domingos com Trabalho ou
 *  Disposição na janela, as duas sequências atuais e as viagens do ERP. */
export interface PainelIndicadoresMotorista {
  domingos_trabalhados: number
  domingos_seguidos: number
  dias_seguidos: number
  viagens: number
}

/** A macro de veículo vazio que o Painel oferece confirmar como Disponível. */
export interface PainelMacroVazio {
  /** Hora em que o veículo ficou vazio: a DATA e o HORARIO digitados, ou a hora de
   *  envio quando a macro não os traz. Null quando a macro é só o aviso de falta de
   *  sinal, e aí não há o que confirmar. */
  vazio_em: string | null
  /** Hora em que a mensagem saiu do terminal. */
  recebida_em: string
  fora_de_cobertura: boolean
  /** A hora veio do que o motorista digitou, e não da hora de envio. */
  hora_digitada: boolean
  texto: string
}

export interface PainelDescargaBoard {
  itens: PainelDescargaRow[]
  ref_date: string
  total: number
  page: number
  per_page: number
  pages: number
  status_counts: Record<string, number>
  /** Quebra do MESMO recorte filtrado pela modalidade do cavalo, da maior para a
   *  menor. Soma o `total` por construção: conjunto sem cavalo, ou com cavalo ainda
   *  sem tipo, vem com `code` nulo em vez de ficar de fora da conta. `label` é o
   *  rótulo da categoria como o ERP publicou, para modalidade que o HUB ainda não
   *  traduz. Ausente em payload antigo. */
  modalidade_counts?: { code: string | null; label: string | null; total: number }[]
  /** Bases distintas do dia — alimenta o seletor de Base, que não tem lista fixa. */
  bases?: string[]
  /** Duplas que hoje têm conjunto alocado, para o seletor de Dupla. Só elas, e não o
   *  cadastro inteiro: dupla sem conjunto é opção que não filtra nada e sugere uma
   *  distribuição que não existe. Ausente em payload antigo. */
  duplas?: { uuid: string; name: string }[]
  /** Opções dos seletores de Local atual e Modalidade, do dia inteiro e antes dos
   *  filtros. As sentinelas `sem-local-atual` e `sem-modalidade` são o recorte de
   *  quem não tem o dado. Ausentes em payload antigo. */
  locais_atuais?: string[]
  modalidades?: { code: string; label: string | null }[]
  /** Conjuntos por Dupla HUB, em qualquer status e antes dos filtros, com a Não
   *  registrada por último, pela sentinela `sem-dupla` (Samuel, 22/09/2026, §11). */
  dupla_counts?: { uuid: string; name: string | null; total: number }[]
  /** Municípios de origem distintos do dia, para o seletor de Município origem, que
   *  nasce do dado como o de Base (Samuel, 24/09/2026). Ausente em payload antigo. */
  municipios_origem?: string[]
  /** Os dias de Data carregamento da grade, para o seletor (28/09/2026). */
  datas_carregamento?: string[]
  /** A seção Domingos Seguidos da barra de indicadores (Samuel, 23/10/2026): as
   *  cinco faixas, "0" a "3" e "4+", sempre todas, com quantas linhas há em cada uma
   *  sobre o filtro inteiro menos a própria faixa. Ausente em payload antigo. */
  domingos_seguidos_counts?: { faixa: string; total: number }[]
  /** Conjuntos que a busca por placa ou motorista acharia, mas que o Painel deixa de
   *  fora, com o motivo (09/10/2026). Vazio sem busca de texto. Ausente em payload
   *  antigo. */
  fora_do_painel?: PainelForaDoPainel[]
}

/** Conjunto escondido pela régua do Painel: o cavalo de prancha, ou o titular com
 *  função que não é Motorista. Espelha `motivo_fora_do_painel` do backend. */
export interface PainelForaDoPainel {
  conjunto_uuid: string
  name: string | null
  truck_plate: string | null
  driver: string | null
  motivo: 'prancha' | 'funcao'
  funcao: string | null
  funcao_label: string | null
}

/** Um registro do Último Disponível, no histórico do veículo (§6). */
export interface DescargaDisponivelRegistro {
  uuid: string
  vigente: boolean
  truck_plate: string | null
  available_at: string
  status_anterior: string | null
  status_anterior_label: string | null
  viagem: { uuid: string; number: string; trip: string | null } | null
  lancado_por: { uuid: string; name: string } | null
  lancado_em: string
  edicoes: {
    available_at_anterior: string
    available_at_novo: string
    editado_por: { uuid: string; name: string } | null
    editado_em: string
  }[]
}

/** Resposta de GET /painel-descarga/escala-dupla: a pessoa pode escalar este cavalo?
 *  Responde pela Dupla HUB do conjunto e, desde 28/09/2026, pelo status dele no Painel
 *  de Descarga, que precisa ser Disponível ou Adiantando. `motivo` é o texto que a
 *  Frente mostra em vermelho. */
export interface EscalaDupla {
  permitido: boolean
  motivo: string | null
}

/** Item do seletor de Motorista da Frente de Trabalho (GET /painel-descarga/motoristas).
 *  `eligible=true` → Disponível, selecionável direto. `warn=true` → status ≠ Disponível,
 *  selecionável COM confirmação (Samuel): a UI avisa que o motorista já está em `status`
 *  na `frente` no `dia` e escala mesmo assim se confirmado. `blocked=true` → inativo ou
 *  conjunto sem cavalo, nunca selecionável, com `reason` pt-BR. `conjunto_uuid` é o valor
 *  usado para atribuir (motorista+cavalo+carreta); null quando não há conjunto. */
/** Conjunto oferecido no reagendamento em massa da Frente
 *  (GET /shipments/reagendamento-em-massa/conjuntos). `escalavel` aplica as réguas
 *  da escala unitária: Dupla HUB, status no Painel e vínculo. `bloqueio_cadastro` é
 *  só a parte que a troca não muda, dupla e vínculo: o conjunto de uma viagem da
 *  seleção está Escalado e mesmo assim pode ir para outra viagem dela. */
export interface ConjuntoReagendamento {
  conjunto_uuid: string
  nome: string | null
  driver: { uuid: string; name: string }
  truck: { uuid: string; plate: string | null; category: Categoria | null }
  trailer: { uuid: string; plate: string | null; category: Categoria | null } | null
  status: string
  status_label: string
  escalavel: boolean
  motivo: string | null
  bloqueio_cadastro: string | null
  /** Situação de RH que escala com aviso, como Férias e Afastado. */
  aviso: string | null
  escala: {
    shipment_uuid: string
    number: string
    contract_uuid: string | null
    contract_label: string | null
    date: string | null
    mesmo_contrato: boolean
  } | null
  /** O Último Disponível do Painel de Descarga, a ordem da Escala automática
   *  (26/10/2026). Nulo sem registro. */
  ultimo_disponivel: string | null
}

/** Por que o motorista de uma viagem não se mexe no lote. `iniciada` é a viagem
 *  Carregando ou em Trânsito, que entra no lote desde 28/10/2026 mudando só o início. */
export type TravaDoMotorista = 'outra_transportadora' | 'composicao' | 'fora_do_alcance' | 'iniciada'

export interface EscalaDaViagem {
  shipment_uuid: string
  /** O conjunto do cadastro que está na viagem; nulo sem escala ou com a composição trocada. */
  conjunto_uuid: string | null
  motorista_travado: TravaDoMotorista | null
}

export interface ContextoReagendamento {
  conjuntos: ConjuntoReagendamento[]
  viagens: EscalaDaViagem[]
}

/** Um item do POST /shipments/reagendamento-em-massa. Campo ausente não mexe, e
 *  `conjunto_uuid: null` tira a escala. `esperado` é o estado lido ao escolher a
 *  viagem, ecoado literalmente: o servidor recusa se ela mudou desde então. */
export interface ItemReagendamento {
  uuid: string
  esperado: {
    harvest_start_prev: string | null
    driver_uuid: string | null
    truck_uuid: string | null
    trailer_uuid: string | null
  }
  harvest_start_prev?: string
  conjunto_uuid?: string | null
}

export interface ResultadoReagendamento {
  items: Shipment[]
  resumo: {
    total: number
    alteradas: number
    reagendadas: number
    desescaladas: number
    escaladas: number
    farois_zerados: number
  }
}

export interface ErroReagendamento {
  uuid: string | null
  number: string | null
  codigo: string
  mensagem: string
  campos?: string[]
}

export interface MotoristaDisponivel {
  conjunto_uuid: string | null
  driver: { uuid: string; name: string; code: string | null; document: string | null }
  truck_plate: string | null
  trailer1_plate: string | null
  trailer2_plate: string | null
  truck_category: Categoria | null
  trailer1_category: Categoria | null
  trailer2_category: Categoria | null
  status: DescargaStatus | string | null
  status_label: string | null
  eligible: boolean
  /** Nunca selecionável (inativo / sem cavalo / sem disponibilidade registrada). */
  blocked: boolean
  /** Selecionável com confirmação: status ≠ Disponível. */
  warn: boolean
  /** Frente (contrato) onde o motorista já está comprometido, para o aviso. */
  frente: string | null
  /** Dia (ISO) do compromisso atual, para o aviso. */
  dia: string | null
  reason: string | null
}

/** Evento do histórico OPERACIONAL de um conjunto no Painel de Descarga.
 *  `scope` separa os dois domínios: `conjunto` (Disponível, Escalado, Manutenção,
 *  Folga, Afastado…) e `trip` (etapas da viagem, cujo histórico próprio é o da
 *  viagem). O endpoint do Painel só devolve escopo `conjunto`. */
export interface DescargaStatusEvent {
  uuid: string
  ref_date: string
  scope?: 'conjunto' | 'trip'
  from_status: string | null
  from_status_label: string | null
  to_status: string | null
  to_status_label: string | null
  /** Contrato PARA ONDE ele foi escalado. Só vem nos eventos de Escalado — é a
   *  resposta a "escalado para qual contrato". */
  contract?: string | null
  /** Contrato ativo no instante da mudança, quando o evento encerrou o ciclo.
   *  Não é mais exibido no dialog: o contrato que interessa é o do Escalado. */
  prev_contract?: string | null
  /** Local de descarga ativo no instante da mudança, quando o evento encerrou o ciclo. */
  prev_local_descarga?: string | null
  /** Último dia da Folga, só nos eventos de Folga com data (28/09/2026). */
  folga_ate?: string | null
  changed_at: string
  /** null = transição automática do sistema (a UI mostra "Sistema"). */
  changed_by: { uuid: string; name: string } | null
}


/** Um período de função operacional, como `/personnel/<uuid>/funcoes` devolve.
 *  `vigente_ate` nulo é a função corrente. */
export interface PersonnelFuncaoPeriodo {
  uuid: string
  funcao: FuncaoOperacional
  funcao_label: string | null
  funcao_anterior: FuncaoOperacional | null
  funcao_anterior_label: string | null
  vigente_de: string | null
  vigente_ate: string | null
  origem: 'hub' | 'protheus' | 'backfill'
  changed_at: string | null
  autor: string | null
}


// ── Outros Serviços Transporte (Samuel, 01/10/2026) ───────────────────────────

/** Status da viagem no HUB: os códigos da Frente, avançados pela geocerca da origem
 *  e do destino. Atendido é sempre o Disponível do conjunto. */
export type OutroServicoStatus =
  | 'escalado' | 'carregando' | 'transito' | 'descarregando' | 'atendido' | 'nao_atendido'
  /** A diária (08/10/2026): status fixo, sem cavalo, carreta nem destino. */
  | 'diaria'

/** Uma ponta da viagem, do Cadastro de Locais. `sem_cerca` avisa que a geocerca não
 *  tem como avançar o status por ela. */
export interface OutroServicoLocal extends PlaceRef {
  category: string | null
  sem_cerca: boolean
}

/** A viagem do Protheus vinculada, lida ao vivo do espelho. `removida` é a viagem
 *  que sumiu do espelho e não voltou com outro registro. */
export interface OutroServicoViagemProtheus {
  uuid: string
  filial: string
  viagem: string
  /** "0101 / 087645", como a especificação escreve. */
  numero: string
  status_viagem: string | null
  removida: boolean
}

/** Motorista, cavalo ou carreta da linha diferente da viagem vinculada, e desde
 *  08/10/2026 o cliente do Protheus sem Cliente HUB. */
export interface OutroServicoAnomalia {
  campo: 'motorista' | 'cavalo' | 'carreta' | 'cliente'
  hub: string | null
  erp: string | null
}

/** Uma linha do módulo: uma viagem de transporte que não é de espiga nem de prancha. */
export interface OutroServicoTransporte {
  uuid: string
  dupla: { uuid: string; name: string } | null
  data_inicio: string | null
  data_fim: string | null
  conjunto_uuid: string | null
  motorista: PersonRef | null
  truck: FleetRef | null
  trailer: FleetRef | null
  /** A carreta 2 do conjunto de dois reboques (08/10/2026). */
  trailer2: FleetRef | null
  placa: string | null
  carreta: string | null
  carreta2: string | null
  origem: OutroServicoLocal | null
  destino: OutroServicoLocal | null
  status_hub: OutroServicoStatus | null
  escalado_em: string | null
  /** A viagem ativa cujo conjunto não está mais em Outros Serviços no Painel. */
  conjunto_solto: boolean
  viagem_protheus: OutroServicoViagemProtheus | null
  /** O serviço do cadastro a que a descrição do ERP foi alocada, ou o padrão. */
  servico: { uuid: string; nome: string; papel: string | null; por_padrao: boolean } | null
  /** O Serviço HUB, apontado aqui ou no módulo de Viagens, com quem classificou e
   *  quando (08/10/2026). */
  servico_apontado: { uuid: string; nome: string; por: string | null; em: string | null } | null
  /** A viagem vinculada está no genérico Outros e espera a classificação do Serviço HUB. */
  servico_apontavel: boolean
  /** O Cliente HUB da viagem vinculada (08/10/2026), e o nome do cliente no Protheus. */
  cliente_hub: { uuid: string; nome: string } | null
  cliente_protheus: string | null
  customer_uuid: string | null
  /** O ERP passou a dizer depois que a viagem é de Espiga ou de Prancha. */
  servico_incompativel: boolean
  nota_fiscal: string | null
  observacao: string | null
  anomalias: OutroServicoAnomalia[]
  created_by: { uuid: string; name: string } | null
  created_at: string | null
  updated_at: string | null
}

/** Um conjunto que a régua da Frente deixaria escalar agora, completo. */
export interface OutroServicoConjuntoEscalavel {
  uuid: string
  nome: string | null
  motorista: PersonRef | null
  cavalo: FleetRef | null
  carreta: FleetRef | null
  dupla: { uuid: string; name: string } | null
  status: string
  status_label: string
}

/** Uma viagem do espelho que a linha pode vincular. */
export interface OutroServicoViagemCandidata {
  uuid: string
  filial: string
  viagem: string
  numero: string
  status_viagem: string | null
  data_prev_inicio: string | null
  /** A saída e a chegada apontadas no ERP desde 21/10/2026; null antes de a viagem
   *  sair e chegar. São elas que a Data Ini e a Data Fim conferem. */
  data_saida?: string | null
  data_chegada?: string | null
  /** Os botões CT-e e MDF-e da viagem vinculada. */
  documentos?: DocumentosFiscais | null
  placa_veiculo: string | null
  placa_reboque: string | null
  /** A segunda carreta do rodotrem, publicada pelo ERP desde 13/10/2026. */
  placa_2reboque: string | null
  nome_motorista: string | null
  destino: string | null
  fantasia_cliente: string | null
  tipo_servico: string | null
  servico: string | null
  servico_apontado: string | null
  sugestao: boolean
}

/** Um evento do histórico da viagem. */
export interface OutroServicoEvento {
  uuid: string
  tipo: 'criacao' | 'status' | 'conjunto' | 'vinculo' | 'reabertura' | 'exclusao' | 'servico_hub' | 'diaria'
  de: string | null
  para: string | null
  de_label: string | null
  para_label: string | null
  origem: 'manual' | 'geocerca' | 'painel' | 'troca_ponta' | 'reconciliacao'
  nota: string | null
  dados: Record<string, unknown> | null
  changed_at: string | null
  changed_by: { uuid: string; name: string } | null
}

/** As opções dos filtros que não são cadastro. */
export interface OutroServicoOpcoes {
  servicos: { uuid: string; nome: string; interno: boolean }[]
  status_protheus: string[]
}
